import { CommonModule } from '@angular/common';
import { Component, NgZone, ElementRef, HostListener, Input, OnDestroy, TemplateRef, ViewChild, ViewContainerRef, computed, inject, signal } from '@angular/core';
import { CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import { GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  resolveCallMode,
  CallInterface,
  CampaignInterface,
  FullCall,
  IncomingCallPayload,
  PhoneInterface,
  SoftphoneOriginNumberMode,
  SoftphoneOriginNumberOptionDto,
  SoftphoneOriginPreferenceDto,
  SoftphoneBrowserSessionMode,
  SoftphoneBrowserSessionResponseDto,
  SoftphoneBrowserSummaryDto,
  SoftphoneStateDto,
  SoftphoneParticipantSnapshotDto,
  SOFTPHONE_CONTRACT_VERSION,
  TransferActivity,
  UserInterface,
  WorkspaceInterface
} from 'voxr-shared';
import { AppService, IssueReportResponse } from '../../../services/app.service';
import { ActivityService } from '../../../services/activity.service';
import { CallService } from '../../../services/call.service';
import { CampaignService } from '../../../services/campaign.service';
import { UserService } from '../../../services/user.service';
import { WorkspaceService } from '../../../services/workspace.service';
import {
  SoftphoneService,
  SoftphoneCallLeadIntent,
  SoftphonePrepareCallResponse,
  SoftphoneState
} from '../../../services/softphone.service';
import {
  SoftphoneIncomingTransferSummary,
  SoftphoneMirroredConnectionStatus,
  SoftphoneMirroredRuntimeState,
  SoftphoneOwnerCommand,
  SoftphoneSessionActiveCallSummary
} from '../../../services/softphone-session-coordinator.service';
import { SocketService } from '../../../services/socket.service';
import { combineLatest, distinctUntilChanged, firstValueFrom, from, map, of, switchMap } from 'rxjs';
import type { Call, Device } from '@twilio/voice-sdk';
import { TransferInfoComponent, TransferInfoViewModel } from '../../general/transfer-info/transfer-info.component';
import { ReportIssueComponent } from '../../general/report-issue/report-issue.component';
import { normalizeSoftphoneParticipantStatus } from '../../../services/softphone-participants.mapper';
import type { SoftphoneParticipantRow } from '../../../services/softphone-participants.mapper';
import { SoftphoneConferenceSession } from '../../../services/softphone-conference-session';
import { SoftphoneDiagnostics } from '../../../services/softphone-diagnostics';
import { SoftphoneExceptionService } from '../../../services/softphone-exception.service';
import type { SoftphoneDiagnosticEntry, SoftphoneDiagnosticLevel } from '../../../services/softphone-diagnostics';

declare global {
  interface Window {
    Twilio?: {
      Device: typeof Device;
    };
  }
}

type TwilioCallLike = Call;
type TwilioDeviceLike = Device;
type IncomingAutoAnswer = {
  call: TwilioCallLike;
  mode: 'all' | 'campaign';
  userId?: string;
  reason?: 'all-calls' | 'manual' | 'transfer-hybrid' | 'campaign-required';
  handled?: boolean;
  cancelBeep?: () => void;
};
type SoftphoneActionKind =
  | 'outbound-start'
  | 'call'
  | 'caller-id'
  | 'command'
  | 'connection'
  | 'three-way-start'
  | 'mute'
  | 'participant-refresh'
  | 'participant-action'
  | 'participant-end'
  | 'participant-hold'
  | 'conference-end';

const safeOwnerCommandErrors = new Set([
  'Transfer is still connecting. Please try again in a moment.',
  'The active call is no longer available in the owner tab.',
  'The requested call is no longer active. Refresh the soft phone and try again.',
  'The owner tab has an active call and cannot release the soft phone.',
]);

type CallLeadContext = {
  source: 'call-logs' | 'campaign-tasks';
  leadName?: string;
  leadPhone: string;
  leadState?: string;
  preferredFromNumber?: string;
  campaignId?: string;
  previousCallId?: string;
  resolvedFromNumber?: string;
  replacementMessage?: string;
};

type CallerIdPreview = {
  resolvedFromNumber?: string;
  unavailableReason?: string;
};

type SoftphoneTransferContext = {
  incomingType?: IncomingCallPayload['type'];
  handoff?: IncomingCallPayload['handoff'];
  call?: CallInterface;
  campaign?: CampaignInterface;
  transfer?: TransferActivity;
};

type OwnerIncomingTransferContext = SoftphoneTransferContext & {
  workspaceId?: string;
  targetTabId?: string;
  noticeTabId?: string;
};

type SoftphoneConnectionVm = {
  integrationId: string;
  title: string;
  status: SoftphoneMirroredConnectionStatus;
  statusLabel: string;
  issueSummary?: string;
};

type SoftphoneConnectionSummary = {
  totalCount: number;
  connectedCount: number;
  inProgressCount: number;
  failedCount: number;
  partialFailure: boolean;
  allFailed: boolean;
};
type SoftphoneFooterSummaryTone = 'success' | 'warning' | 'danger' | 'secondary';
type SoftphoneCallQualityLevel = 'good' | 'fair' | 'poor' | 'reconnecting';
type SoftphoneCallQuality = {
  level: SoftphoneCallQualityLevel;
  rttMs?: number;
  jitterMs?: number;
  lossPct?: number;
  mos?: number;
};
type SoftphoneFooterSummaryItem = {
  label: 'connected' | 'failed' | 'connecting';
  count: number;
  tone: SoftphoneFooterSummaryTone;
};

type SoftphoneIntegrationOption = {
  integrationId: string;
  title: string;
  tooltip: string;
};

type SoftphoneOutboundStartResult =
  | { ok: true; }
  | { ok: false; error: string; };

type SoftphoneRuntimeComponentState = {
  connectionState: SoftphoneState;
  detail?: string;
  session: SoftphoneMirroredRuntimeState;
  config?: SoftphoneStateDto;
  browserSessionMode: SoftphoneBrowserSessionMode | 'unverified';
  activeBrowser?: SoftphoneBrowserSummaryDto;
  switchingBrowser: boolean;
  initializing: boolean;
  destroying: boolean;
};

type SoftphonePanelComponentState = {
  open: boolean;
  collapsed: boolean;
  outboundSettingsCollapsed: boolean;
  dtmfExpanded: boolean;
  overlayPosition: { left: number; top: number };
};

type SoftphoneDialerComponentState = {
  destination: string;
  originQuery: string;
  originMode: SoftphoneOriginNumberMode;
  selectedOriginNumberId?: string;
  originPreference?: SoftphoneOriginPreferenceDto | null;
  originNumberOptions: SoftphoneOriginNumberOptionDto[];
  callerIdPreview?: CallerIdPreview;
  loadingOriginNumberOptions: boolean;
  savingOriginPreference: boolean;
  loadingCallerIdPreview: boolean;
  resolvingFromNumber: boolean;
  preparingOutgoing: boolean;
};

type SoftphoneCallProgress = 'idle' | 'dialing' | 'ringing' | 'connected' | 'ended';

type SoftphoneCallComponentState = {
  incoming?: SoftphoneIncomingTransferSummary;
  active?: SoftphoneSessionActiveCallSummary;
  mirroredIncoming?: SoftphoneIncomingTransferSummary;
  incomingNotice?: SoftphoneIncomingTransferSummary;
  remoteActive?: SoftphoneSessionActiveCallSummary;
  activeTransfer?: TransferActivity;
  transferContext?: SoftphoneTransferContext;
  leadContext?: CallLeadContext;
  progress: SoftphoneCallProgress;
  muted: boolean;
  onHold: boolean;
  mainStartedAt?: number;
  childLegStartedAt?: number;
  mainDurationSeconds: number;
  childLegDurationSeconds: number;
  threeWayAccepted: boolean;
  voxrCallId?: string;
  callerIdMessage?: string;
};

type SoftphoneDiagnosticsComponentState = {
  lastTokenFetchAt?: string;
  reconnectCount: number;
  recentSdkEvents: SoftphoneDiagnosticEntry[];
};

@Component({
  selector: 'app-softphone',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    MatProgressBarModule,
    TransferInfoComponent,
    ReportIssueComponent,
  ],
  templateUrl: './softphone.component.html',
  styleUrl: './softphone.component.scss',
})
export class SoftphoneComponent implements OnDestroy {
  private readonly readyDetail = 'Ready to receive transfers';
  private readonly outboundConnectTimeoutMs = 30_000;
  private readonly networkDropWindowMs = 10_000;
  private readonly qualityMetricRefreshMs = 5_000;
  private readonly networkDropMessage = 'Your connection dropped and the call ended. Check your network before the next call.';
  private readonly outboundConnectTimeoutMessage = 'The call could not start within 30 seconds. Please try again.';
  private ownerFocusNotification?: Notification;
  private activityService = inject(ActivityService);
  private callService = inject(CallService);
  private campaignService = inject(CampaignService);
  private userService = inject(UserService);
  private app = inject(AppService);
  private workspaceService = inject(WorkspaceService);
  private softphoneService = inject(SoftphoneService);
  private exceptions = inject(SoftphoneExceptionService);
  private socketService = inject(SocketService);
  private dialog = inject(MatDialog);
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private readonly threeWayNumberStorageKey = 'voxr.softphone.three-way-number';
  private readonly overlayPositionStorageKey = 'voxr.softphone.overlay-position';
  private readonly outboundSettingsCollapsedStorageKey = 'voxr.softphone.outbound-settings-collapsed';
  private readonly overlayWidth = 420;
  private readonly overlayEstimatedHeight = 560;
  private readonly overlayMargin = 12;
  readonly contractVersionMismatch = computed(() => {
    const serverVersion = this.softphoneService.serverContractVersion();
    return serverVersion !== undefined && serverVersion !== SOFTPHONE_CONTRACT_VERSION;
  });
  private overlayRef?: OverlayRef;
  private overlayPortal?: TemplatePortal;
  private overlayPositionStrategy?: GlobalPositionStrategy;

  @Input() showLauncher = true;
  @ViewChild('softphoneOpener') softphoneOpener?: ElementRef<HTMLElement>;
  @ViewChild('softphoneOverlayContent', { static: true }) softphoneOverlayContent!: TemplateRef<unknown>;
  @ViewChild('connectionDetailsDialog', { static: true }) connectionDetailsDialog!: TemplateRef<unknown>;
  @ViewChild('tabControlDialog', { static: true }) tabControlDialog!: TemplateRef<unknown>;
  @ViewChild('browserSessionDialog', { static: true }) browserSessionDialog!: TemplateRef<unknown>;
  @ViewChild('endConferenceConfirmation', { static: true }) endConferenceConfirmation!: TemplateRef<unknown>;
  @ViewChild('leaveConferenceConfirmation', { static: true }) leaveConferenceConfirmation!: TemplateRef<unknown>;

  user = signal<UserInterface | undefined>(undefined);
  readonly autoAnswerMode = computed(() => this.user()?.agentInfo?.liveTransfer?.softPhone?.autoAnswerMode || 'campaign');
  currentWorkspace = signal<WorkspaceInterface | undefined>(undefined);
  readonly runtimeState = signal<SoftphoneRuntimeComponentState>({
    connectionState: 'inactive',
    detail: 'Soft phone is off',
    session: {
      tabId: '',
      isOwner: false,
      state: 'inactive',
      detail: 'Soft phone is off',
    },
    browserSessionMode: 'unverified',
    switchingBrowser: false,
    initializing: false,
    destroying: false,
  });
  readonly panelState = signal<SoftphonePanelComponentState>({
    open: false,
    collapsed: false,
    outboundSettingsCollapsed: false,
    dtmfExpanded: false,
    overlayPosition: { left: this.overlayMargin, top: this.overlayMargin },
  });
  readonly dialerState = signal<SoftphoneDialerComponentState>({
    destination: '',
    originQuery: '',
    originMode: 'local-presence',
    originNumberOptions: [],
    loadingOriginNumberOptions: false,
    savingOriginPreference: false,
    loadingCallerIdPreview: false,
    resolvingFromNumber: false,
    preparingOutgoing: false,
  });
  readonly callState = signal<SoftphoneCallComponentState>({
    progress: 'idle',
    muted: false,
    onHold: false,
    mainDurationSeconds: 0,
    childLegDurationSeconds: 0,
    threeWayAccepted: false,
  });
  readonly diagnosticsState = signal<SoftphoneDiagnosticsComponentState>({
    reconnectCount: 0,
    recentSdkEvents: [],
  });
  readonly state = computed(() => this.runtimeState().connectionState);
  readonly stateDetail = computed(() => this.runtimeState().detail);
  readonly sessionState = computed(() => this.runtimeState().session);
  readonly softphoneStateDto = computed(() => this.runtimeState().config);
  readonly browserSessionMode = computed(() => this.runtimeState().browserSessionMode);
  readonly activeBrowserSession = computed(() => this.runtimeState().activeBrowser);
  readonly switchingBrowserSession = computed(() => this.runtimeState().switchingBrowser);
  threeWayNumber = signal('');
  workspacePhones: PhoneInterface[] = [];
  dialButtons = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
  placingThreeWay = signal(false);
  threeWayConnectionRecovering = signal(false);
  readonly callQuality = signal<SoftphoneCallQuality | undefined>(undefined);
  readonly callQualityLabel = computed(() => {
    switch (this.callQuality()?.level) {
      case 'good': return 'Good connection';
      case 'fair': return 'Fair connection';
      case 'poor': return 'Poor connection';
      case 'reconnecting': return 'Reconnecting…';
      default: return '';
    }
  });
  readonly callQualityDetail = computed(() => {
    const quality = this.callQuality();
    if (!quality) return '';
    const parts: string[] = [];
    if (quality.rttMs !== undefined) parts.push(`Latency ${quality.rttMs} ms`);
    if (quality.jitterMs !== undefined) parts.push(`Jitter ${quality.jitterMs} ms`);
    if (quality.lossPct !== undefined) parts.push(`Loss ${quality.lossPct.toFixed(1)}%`);
    if (quality.mos !== undefined) parts.push(`Quality ${quality.mos.toFixed(1)}/5`);
    return parts.join(' · ');
  });
  private originPreferenceRequestVersion = 0;
  private callerIdPreviewRequestVersion = 0;

  private device?: TwilioDeviceLike;
  private ownerIncomingCall?: TwilioCallLike;
  private reconciledOwnerIncomingCall?: TwilioCallLike;
  private incomingAutoAnswer?: IncomingAutoAnswer;
  private activeCall = signal<TwilioCallLike | undefined>(undefined);
  private incomingCall = signal<TwilioCallLike | undefined>(undefined);
  readonly mirroredIncomingTransfer = computed(() => this.callState().mirroredIncoming);
  readonly incomingTransferNotice = computed(() => this.callState().incomingNotice);
  private mirroredIncomingTransferTimeout?: ReturnType<typeof setTimeout>;
  private mirroredIncomingTransferKey?: string;
  private pendingRemoteAcceptedCall?: {
    transferId?: string;
    callId?: string;
    previousState: SoftphoneState;
    previousDetail?: string;
  };
  private pendingRemoteAcceptedCallTimeout?: ReturnType<typeof setTimeout>;
  private pendingOutboundOwnerCommand?: {
    messageId: string;
    deadlineAt: number;
    timeout: ReturnType<typeof setTimeout>;
    callId?: string;
  };
  private uiTimer?: ReturnType<typeof setInterval>;
  private wakeLockSentinel?: any;
  private wakeLockRequest?: Promise<void>;
  private wakeLockGeneration = 0;
  readonly wakeLockSupported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  private registeredDevice?: TwilioDeviceLike;
  private connectionDetailsDialogRef?: MatDialogRef<unknown>;
  private browserSessionDialogRef?: MatDialogRef<unknown>;
  private tabControlDialogRef?: MatDialogRef<unknown>;

  private diagnostics = new SoftphoneDiagnostics();
  private zone = inject(NgZone);
  private qualityCalls = new Map<TwilioCallLike, {
    id: string; device?: TwilioDeviceLike; cleanup: () => void;
    // Per-call end attribution: set when the agent ends the call, and while the connection is recovering.
    localEnd?: boolean; issueActive?: boolean; issueAt?: number;
  }>();
  private qualityDevices = new Map<TwilioDeviceLike, { references: number; listener: () => void }>();
  private activeQualityWarnings = new Set<string>();
  private lastQualitySample?: Record<string, unknown>;
  private lastQualityWriteAt = 0;
  private automaticReport?: { id: string; abort: AbortController; timer: ReturnType<typeof setTimeout> };
  private qualityUserId?: string;

  readonly activeTransfer = computed(() => this.callState().activeTransfer);
  readonly transferContext = computed(() => this.callState().transferContext);
  private ownerIncomingTransferContext?: OwnerIncomingTransferContext;
  private incomingFallback?: {
    call: TwilioCallLike;
    timer?: ReturnType<typeof setTimeout>;
    startedAt: number;
    shown: boolean;
    actionTaken?: boolean;
  };
  readonly callLeadContext = computed(() => this.callState().leadContext);
  readonly callProgress = computed(() => this.callState().progress);
  private readonly conferenceSession = new SoftphoneConferenceSession(this.softphoneService, {
    context: () => ({
      callId: this.getActiveCallIdForParticipantOps(),
      activityId: this.getActiveTransferId(),
      agentCallSid: this.getCallSid(this.activeCall()),
      inferEndedAt: true,
      mainStartedAt: this.callState().mainStartedAt,
      thirdStartedAt: this.callState().childLegStartedAt,
      mainStatus: this.callProgress() === 'idle' ? undefined : this.callProgress(),
    }),
    rowsApplied: rows => this.applyConferenceTiming(rows),
  });
  participantRows = this.conferenceSession.rows;
  pendingAgentHoldAction = signal(false);
  private fromNumberResolutionPromise?: Promise<string | undefined>;
  readonly conferenceMutationPending = computed(() =>
    this.placingThreeWay()
    || this.conferenceSession.mutationPending()
    || this.pendingAgentHoldAction()
  );
  readonly hasConferenceParticipants = computed(() => this.participantRows().length > 0);
  readonly mainParticipantLeg = computed(() =>
    this.participantRows().find(row => row.legType === 'main')
  );
  readonly thirdParticipantLeg = this.conferenceSession.thirdParticipantLeg;
  readonly thirdParticipantEnded = this.conferenceSession.thirdParticipantEnded;
  readonly hasMainParticipantLeg = computed(() => !!this.mainParticipantLeg()?.callSid);
  readonly hasThirdParticipantLeg = computed(() => !!this.thirdParticipantLeg()?.callSid);
  private readonly mainMissing = computed(() =>
    !this.hasMainParticipantLeg()
    && this.participantRows().some(row => row.legType === 'agent')
    && this.participantRows().some(row => row.legType === 'third')
  );
  readonly mainParticipantDisplayLeg = computed<SoftphoneParticipantRow | undefined>(() => this.getMainDisplayLeg());

  private getMainDisplayLeg(): SoftphoneParticipantRow | undefined {
    const mainLeg = this.mainParticipantLeg();
    if (mainLeg) return mainLeg;
    if (this.mainMissing()) return undefined;
    if (!this.activeCall() || this.callProgress() === 'idle' || this.callProgress() === 'ended') return undefined;

    return {
      callSid: '',
      status: normalizeSoftphoneParticipantStatus(this.callProgress()),
      startedAt: this.callState().mainStartedAt,
      endedAt: this.callProgress() === 'ended' ? Date.now() : undefined,
      legType: 'main',
    };
  }

  readonly enabled = computed(() => !!this.softphoneStateDto()?.enabled);
  readonly routeArea = signal<string | undefined>(this.app.routeArea);
  readonly isWorkspaceRoute = computed(() => this.routeArea() === 'workspace');
  readonly connectionRows = computed<SoftphoneConnectionVm[]>(() =>
    (this.sessionState().connections || []).map(connection => ({
      integrationId: connection.integrationId,
      title: connection.title?.trim() || connection.integrationId,
      status: this.getConnectionDisplayStatus(connection.status),
      statusLabel: this.getConnectionStatusLabel(this.getConnectionDisplayStatus(connection.status)),
      issueSummary: this.getConnectionIssueSummary(this.getConnectionDisplayStatus(connection.status), connection.error),
    }))
  );
  readonly connectionSummary = computed<SoftphoneConnectionSummary>(() => this.summarizeConnections(this.connectionRows()));
  readonly isMultiConnection = computed(() => this.connectionSummary().totalCount > 1);
  readonly showFooterConnectionSummary = computed(() => this.isMultiConnection() && this.connectionSummary().totalCount > 0);
  readonly showSingleConnectionErrorCard = computed(() =>
    this.connectionSummary().totalCount === 1 && this.connectionSummary().failedCount === 1
  );
  readonly hasConnectionIssues = computed(() => this.connectionSummary().failedCount > 0);
  readonly checkingSetup = signal(false);
  readonly setupIssue = computed(() => this.enabled() && this.state() === 'inactive'
    && this.browserSessionMode() === 'active' && !this.contractVersionMismatch()
    && !this.ownerRecovery() && !this.hasAnyCall() && !this.softphoneStateDto()?.activeCall
    && this.connectionSummary().totalCount === 0 ? this.sessionState().setupIssue : undefined);
  readonly setupIssueTitle = computed(() => this.setupIssue() === 'no-eligible-campaign'
    ? 'No active or paused campaign' : 'Calling setup needs attention');
  readonly setupIssueMessage = computed(() => this.setupIssue() === 'no-eligible-campaign'
    ? 'Your softphone is on, but none of the campaigns available to you are active or paused. Ask your workspace admin to review your campaign access and status.'
    : 'Your softphone is on, but its calling connection is unavailable. Ask your workspace admin to review the campaign\'s calling setup.');
  readonly statusTone = computed<'success' | 'warning' | 'danger' | 'secondary'>(() => {
    const summary = this.connectionSummary();
    if (this.setupIssue()) return 'warning';
    if (summary.partialFailure) return 'warning';
    if (summary.allFailed || (summary.failedCount > 0 && summary.connectedCount === 0)) return 'danger';
    if (summary.connectedCount > 0) return 'success';
    if (summary.inProgressCount > 0) return 'warning';
    if (this.state() === 'error') return 'danger';
    if (this.state() === 'loading') return 'warning';
    return 'secondary';
  });
  readonly statusText = computed(() => {
    if (this.browserSessionMode() === 'standby' || this.browserSessionMode() === 'blocked') return 'Standby';
    if (this.browserSessionMode() === 'unverified') return 'Error';
    if (this.setupIssue()) return 'Setup required';
    if (this.sessionState().ownerLifecycleStatus === 'recovering' && this.state() !== 'ready') return 'Reconnecting';
    const summary = this.connectionSummary();
    if (this.state() === 'ready' && !this.sessionState().isOwner && !summary.partialFailure) return 'Ready via another tab';
    if (summary.totalCount > 1) {
      if (summary.connectedCount > 0) return `${summary.connectedCount} of ${summary.totalCount} connected`;
      if (summary.inProgressCount > 0) return 'Connecting';
      if (summary.allFailed) return 'Error';
    }
    if (summary.totalCount === 1) {
      if (summary.connectedCount > 0) return 'Ready';
      if (summary.failedCount > 0) return 'Error';
      if (summary.inProgressCount > 0) return 'Connecting';
    }

    switch (this.state()) {
      case 'ready':
        return 'Ready';
      case 'loading':
        return 'Connecting';
      case 'error':
        return 'Error';
      default:
        return 'Inactive';
    }
  });
  readonly connectionSummaryCounts = computed(() => [
    { label: 'connected', count: this.connectionSummary().connectedCount },
    { label: 'failed', count: this.connectionSummary().failedCount },
    { label: 'connecting', count: this.connectionSummary().inProgressCount },
  ]);
  readonly footerSummaryTone = computed<SoftphoneFooterSummaryTone>(() => {
    const summary = this.connectionSummary();
    if (summary.allFailed || (summary.failedCount > 0 && summary.connectedCount === 0 && summary.inProgressCount === 0)) {
      return 'danger';
    }
    if (summary.failedCount > 0 || summary.inProgressCount > 0) {
      return 'warning';
    }
    if (summary.connectedCount > 0) {
      return 'success';
    }
    return 'secondary';
  });
  readonly footerSummaryIcon = computed(() => {
    const summary = this.connectionSummary();
    if (summary.allFailed || (summary.failedCount > 0 && summary.connectedCount === 0 && summary.inProgressCount === 0)) {
      return 'error_outline';
    }
    if (summary.failedCount > 0) {
      return 'warning';
    }
    if (summary.inProgressCount > 0 && summary.connectedCount === 0) {
      return 'sync';
    }
    if (summary.connectedCount > 0) {
      return 'check_circle';
    }
    return 'radio_button_checked';
  });
  readonly footerSummaryItems = computed<SoftphoneFooterSummaryItem[]>(() => {
    const summary = this.connectionSummary();
    const items: SoftphoneFooterSummaryItem[] = [];

    if (summary.failedCount > 0) {
      items.push({ label: 'failed', count: summary.failedCount, tone: 'danger' });
    }
    if (summary.inProgressCount > 0) {
      items.push({ label: 'connecting', count: summary.inProgressCount, tone: summary.failedCount > 0 ? 'warning' : 'secondary' });
    }
    if (summary.connectedCount > 0 || items.length === 0) {
      items.push({
        label: 'connected',
        count: summary.connectedCount,
        tone: summary.failedCount === 0 && summary.inProgressCount === 0 ? 'success' : 'secondary',
      });
    }

    return items;
  });
  readonly primaryStatusMessage = computed(() => {
    const summary = this.connectionSummary();
    if (summary.allFailed || this.showSingleConnectionErrorCard()) {
      return this.getFriendlyPrimaryErrorMessage(
        this.connectionRows()
          .map(connection => connection.issueSummary)
          .find(Boolean)
      );
    }
    return this.stateDetail();
  });
  readonly failedConnectionIds = computed(() =>
    this.connectionRows()
      .filter(connection => connection.status === 'error' || connection.status === 'disconnected')
      .map(connection => connection.integrationId)
  );
  readonly hasIncomingTransfer = computed(() => !!this.incomingCall() || !!this.mirroredIncomingTransfer());
  readonly canHandleIncomingTransfer = computed(() => !!this.incomingCall()
    || (!!this.mirroredIncomingTransfer() && this.softphoneService.canDelegateToOwner));
  readonly canDial = computed(() => this.canManualCall() && this.state() === 'ready' && !this.hasAnyCall());
  readonly hasAnyCall = computed(() =>
    !!this.activeCall() || !!this.callState().remoteActive || this.hasIncomingTransfer() || !!this.incomingTransferNotice()
  );
  readonly outboundBlockedOutsideWorkspace = computed(() =>
    this.enabled() && !this.hasAnyCall() && !this.isWorkspaceRoute()
  );
  readonly canShowThreeWaySection = computed(() => !!this.activeCall() && this.callProgress() === 'connected');
  readonly showThreeWayLoading = computed(() => this.placingThreeWay() && this.callProgress() === 'connected' && !!this.activeCall());
  readonly hasConferenceContext = computed(() =>
    this.hasConferenceParticipants() || this.showThreeWayLoading()
  );
  readonly showMainParticipantLoading = computed(() =>
    this.conferenceSession.loading() && this.hasConferenceParticipants() && !this.hasMainParticipantLeg()
  );
  readonly showThirdParticipantLoading = computed(() =>
    this.showThreeWayLoading() || (this.conferenceSession.loading() && this.hasConferenceContext() && !this.hasThirdParticipantLeg())
  );
  readonly mainParticipantStatusLabel = computed(() =>
    this.mainParticipantDisplayLeg()?.status || '--'
  );
  readonly thirdParticipantStatusLabel = computed(() => {
    const third = this.thirdParticipantLeg();
    if (this.thirdParticipantEnded()) return 'Ended';
    if (third?.status) return third.status;
    if (this.showThreeWayLoading()) return 'ringing';
    return '--';
  });
  readonly panelModeText = computed<string | undefined>(() => {
    if (this.hasIncomingTransfer() || this.incomingTransferNotice()) return this.transferContext() ? 'Incoming transfer' : 'Incoming call';
    if (this.activeCall()) return 'Active call';
    if (this.callLeadContext()) return 'Follow-up call';
    if (this.state() === 'ready'
      && !this.sessionState().isOwner
      && this.connectionSummary().partialFailure) return 'Via another tab';
    return undefined;
  });
  readonly loadingMessage = computed(() => {
    if (this.dialerState().savingOriginPreference) return 'Saving caller ID preference...';
    if (this.dialerState().loadingCallerIdPreview) return 'Refreshing caller ID preview...';
    if (this.showThreeWayLoading()) return 'Starting three-way call...';
    if (this.dialerState().resolvingFromNumber) return 'Resolving caller ID...';
    if (this.dialerState().preparingOutgoing) return 'Starting outbound call...';
    if (this.state() === 'loading' && this.connectionSummary().failedCount === 0) return 'Connecting soft phone...';
    return undefined;
  });
  readonly loadingState = computed(() => {
    const message = this.loadingMessage();
    return {
      active: !!message || this.conferenceSession.loading() || this.conferenceSession.ending(),
      message,
      showProgressBar: this.dialerState().resolvingFromNumber || this.showThreeWayLoading() || this.dialerState().preparingOutgoing || this.state() === 'loading',
      participantsRefreshing: this.conferenceSession.loading(),
      pendingParticipantCallSid: this.conferenceSession.pendingParticipantCallSid(),
      pendingAgentHoldAction: this.pendingAgentHoldAction(),
      endingConference: this.conferenceSession.ending(),
    };
  });
  readonly ownerRecovery = computed(() => this.sessionState().ownerRecovery);
  readonly callButtonLabel = computed(() => this.callLeadContext() ? 'Call lead' : 'Call');
  readonly integrationOptions = computed<SoftphoneIntegrationOption[]>(() =>
    (this.softphoneStateDto()?.resolvedIntegrations || []).map(connection => ({
      integrationId: connection.integrationId,
      title: connection.title?.trim() || connection.integrationId,
      tooltip: this.buildIntegrationOptionTooltip(connection.title?.trim() || connection.integrationId, connection.integrationId, connection.accountSid),
    }))
  );
  readonly selectedIntegrationId = computed(() =>
    this.softphoneService.selectedIntegrationId()
    || this.integrationOptions()[0]?.integrationId
  );
  readonly selectedIntegrationOption = computed<SoftphoneIntegrationOption | undefined>(() =>
    this.integrationOptions().find(option => option.integrationId === this.selectedIntegrationId())
    || this.integrationOptions()[0]
  );
  readonly selectedCapabilities = computed(() => this.softphoneStateDto()?.resolvedIntegrations
    ?.find(connection => connection.integrationId === this.selectedIntegrationId())?.capabilities);
  readonly canManualCall = computed(() => this.isWorkspaceRoute() && !!this.selectedCapabilities()?.manualCall);
  readonly canOriginPreferences = computed(() => this.isWorkspaceRoute() && !!this.selectedCapabilities()?.originPreferences);
  readonly hasCoachTransport = computed(() => !!this.softphoneStateDto()?.resolvedIntegrations
    ?.some(connection => connection.capabilities?.connect && connection.capabilities?.coach));
  readonly selectedConnectionStatus = computed(() =>
    this.connectionRows().find(connection => connection.integrationId === this.selectedIntegrationId())?.status
  );
  readonly previewBlocked = computed(() => {
    const status = this.selectedConnectionStatus();
    return status === 'error' || status === 'disconnected';
  });
  readonly callReady = computed(() => this.selectedConnectionStatus() === 'connected');
  readonly selectedOriginNumber = computed(() =>
    this.dialerState().originNumberOptions.find(option => option.phoneNumberId === this.dialerState().selectedOriginNumberId)
  );
  readonly collapsedOutboundSettingsSummary = computed(() => {
    const integrationTitle = this.selectedIntegrationOption()?.title || 'Not selected';
    const callerIdSummary = this.dialerState().originMode === 'specific-number'
      ? (this.selectedOriginNumber()?.phoneNumber
        ? this.formatPhoneForDisplay(this.selectedOriginNumber()?.phoneNumber)
        : 'Specific number')
      : 'Local presence';

    return {
      integration: `Integration: ${integrationTitle}`,
      callerId: `Caller ID: ${callerIdSummary}`,
    };
  });
  readonly callerIdPreviewTarget = computed(() =>
    this.callLeadContext()?.leadPhone || this.normalizeDialTarget(this.dialerState().destination)
  );
  readonly transferInfo = computed<TransferInfoViewModel | undefined>(() => {
    const context = this.transferContext();
    if (!context) return undefined;

    const contactInfo = context.call?.contactInfo;
    const leadName = [contactInfo?.firstName, contactInfo?.lastName].filter(Boolean).join(' ').trim() || undefined;
    const leadPhone = context.call?.flow === 'INBOUND'
      ? context.call?.fromNumber || contactInfo?.phone || context.transfer?.from
      : context.call?.toNumber || contactInfo?.phone || context.transfer?.from;
    const summary = context.call?.summary;
    const campaignName = context.campaign?.name || (this.activeTransfer()?._metadata as any)?.campaign?.name;
    const recoveryStatusLabel = context.incomingType === 'recovery' ? ({
      missed: 'Original transfer: Missed',
      dropped: 'Original transfer: Dropped',
      failed: 'Original transfer: Failed',
    } as const)[context.transfer?.status as 'missed' | 'dropped' | 'failed'] : undefined;

    if (!leadName && !leadPhone && !campaignName && !summary && !context.handoff) return undefined;

    return {
      handoffId: `${context.call?._id || ''}:${context.transfer?._id || ''}`,
      handoff: context.handoff,
      callMode: context.call ? resolveCallMode(context.call) : undefined,
      leadName,
      leadPhone,
      campaignName,
      summary,
      statusLabel: context.transfer?.data?.['directOutbound'] === true ? 'Unqualified direct call' : recoveryStatusLabel,
    };
  });
  readonly shouldShowTransferInfoOnActiveCall = computed(() => !!this.activeCall() && !!this.transferInfo());
  readonly lastSeenText = computed(() => {
    const lastSeenAt = this.softphoneStateDto()?.resolvedIntegrations
      ?.map(connection => connection.lastSeenAt ? new Date(connection.lastSeenAt).getTime() : NaN)
      .filter(value => Number.isFinite(value))
      .sort((left, right) => right - left)?.[0];
    if (!lastSeenAt) return 'Never';
    return new Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(lastSeenAt));
  });
  readonly activeTransferMode = computed(() =>
    this.transferContext()?.incomingType === 'recovery' ? undefined
      : this.activeTransfer()?.transferMode?.mode || this.transferContext()?.transfer?.transferMode?.mode
  );
  readonly isColdTransfer = computed(() => this.activeTransferMode() === 'cold');
  readonly threeWayReadinessReason = computed(() => {
    if (!this.activeCall() || this.callProgress() !== 'connected') return 'call-not-connected';
    if (this.threeWayConnectionRecovering()) return 'connection-recovering';
    if (this.callState().threeWayAccepted || this.hasThirdParticipantLeg()) return 'third-party-exists';
    if (!this.normalizeDialTarget(this.threeWayNumber())) return 'number-required';
    if (this.conferenceMutationPending()) return 'mutation-pending';
    return 'ready';
  });
  readonly canStartThreeWay = computed(() => this.threeWayReadinessReason() === 'ready');
  readonly activeCallStatusLabel = computed(() => {
    switch (this.callProgress()) {
      case 'dialing': return 'Dialing';
      case 'ringing': return 'Ringing';
      case 'connected': return 'Connected';
      case 'ended': return 'Ended';
      default: return 'Ready';
    }
  });
  readonly mainCallTimerText = computed(() => this.formatDuration(this.callState().mainDurationSeconds));
  readonly childLegTimerText = computed(() => this.formatDuration(this.callState().childLegDurationSeconds));
  readonly mainParticipantTimerText = computed(() => {
    const participant = this.mainParticipantDisplayLeg();
    if (!participant?.startedAt) return '--:--';
    if (participant.endedAt) {
      const elapsedSeconds = Math.floor((participant.endedAt - participant.startedAt) / 1000);
      return this.formatDuration(elapsedSeconds);
    }
    return this.mainCallTimerText();
  });
  readonly thirdParticipantTimerText = computed(() => {
    const participant = this.thirdParticipantLeg();
    if (!participant?.startedAt) return '--:--';
    if (this.thirdParticipantEnded() && !participant.endedAt) return '--:--';
    if (participant.endedAt) {
      const elapsedSeconds = Math.max(0, Math.floor((participant.endedAt - participant.startedAt) / 1000));
      return this.formatDuration(elapsedSeconds);
    }
    return this.childLegTimerText();
  });
  readonly hasStartedThreeWayLeg = computed(() =>
    this.callState().threeWayAccepted || this.placingThreeWay() || !!this.thirdParticipantLeg()
  );
  readonly isThirdLegConnected = computed(() =>
    this.thirdParticipantStatusLabel() === 'connected'
  );
  readonly canToggleMute = computed(() => {
    const call = this.activeCall() as any;
    return !!call && typeof call.mute === 'function' && !this.conferenceMutationPending();
  });
  readonly canToggleHold = computed(() =>
    !!this.activeCall()
    && this.callProgress() === 'connected'
    && !!this.getActiveCallIdForParticipantOps()
    && !!this.getCallSid(this.activeCall())
    && !this.conferenceMutationPending()
  );
  readonly canEndConference = computed(() =>
    !!this.getActiveCallIdForParticipantOps() && this.participantRows().length > 0 && !this.conferenceMutationPending()
  );
  readonly conferenceLineCount = computed(() => this.getConferenceLineCount());

  private getConferenceLineCount() {
    let count = this.activeCall() ? 1 : 0; // agent leg
    const rows = this.participantRows();

    const mainConnected = rows.some(row =>
      row.legType === 'main' && !row.endedAt && normalizeSoftphoneParticipantStatus(row.status) !== 'ended'
    );
    const thirdConnected = rows.some(row =>
      row.legType === 'third' && !row.endedAt && normalizeSoftphoneParticipantStatus(row.status) !== 'ended'
    );

    if (mainConnected || (!this.mainMissing() && this.callProgress() === 'connected')) count += 1;
    if (thirdConnected) count += 1;

    return Math.max(1, count);
  }
  readonly conferenceOnLineText = computed(() => `${this.conferenceLineCount()} on the line`);
  readonly activeCallDisplayName = computed(() => {
    const context = this.callLeadContext();
    if (context?.leadName) return context.leadName;
    if (context?.leadPhone) return this.formatPhoneForDisplay(context.leadPhone);

    const mainTarget = this.mainParticipantDisplayLeg()?.targetNumber;
    if (mainTarget) return this.formatPhoneForDisplay(mainTarget);

    const dialTarget = this.dialerState().destination;
    return dialTarget ? this.formatPhoneForDisplay(dialTarget) : 'Active call';
  });
  readonly activeCallDisplayDetail = computed(() => {
    const context = this.callLeadContext();
    if (context?.leadPhone && context.leadName) return this.formatPhoneForDisplay(context.leadPhone);
    const mainTarget = this.mainParticipantDisplayLeg()?.targetNumber;
    return mainTarget ? this.formatPhoneForDisplay(mainTarget) : this.activeCallStatusLabel();
  });

  private transitionRuntimeConnection(connectionState: SoftphoneState) {
    this.runtimeState.update(current => ({ ...current, connectionState }));
  }

  private updateRuntimeDetail(detail?: string) {
    this.runtimeState.update(current => ({ ...current, detail }));
  }

  private applyMirroredRuntime(session: SoftphoneMirroredRuntimeState) {
    this.runtimeState.update(current => ({
      ...current,
      session,
      browserSessionMode: !session.isOwner && session.browserSessionMode
        ? session.browserSessionMode
        : current.browserSessionMode,
      activeBrowser: !session.isOwner && session.browserSessionMode
        ? session.activeBrowser
        : current.activeBrowser,
    }));
  }

  private updateBrowserSession(
    browserSessionMode: SoftphoneBrowserSessionMode | 'unverified',
    activeBrowser = this.runtimeState().activeBrowser,
  ) {
    this.runtimeState.update(current => ({ ...current, browserSessionMode, activeBrowser }));
  }

  private beginBrowserSwitch() {
    this.runtimeState.update(current => ({ ...current, switchingBrowser: true }));
  }

  private finishBrowserSwitch() {
    this.runtimeState.update(current => ({ ...current, switchingBrowser: false }));
  }

  private beginRuntimeInitialization() {
    this.runtimeState.update(current => ({ ...current, initializing: true }));
  }

  private finishRuntimeInitialization() {
    this.runtimeState.update(current => ({ ...current, initializing: false }));
  }

  private beginRuntimeDestruction() {
    this.runtimeState.update(current => ({ ...current, destroying: true }));
  }

  private finishRuntimeDestruction() {
    this.runtimeState.update(current => ({ ...current, destroying: false }));
  }

  private expandPanel() {
    this.panelState.update(current => ({ ...current, collapsed: false }));
  }

  private setPanelOpen(open: boolean) {
    this.panelState.update(current => ({ ...current, open }));
  }

  private setOverlayPosition(overlayPosition: { left: number; top: number }) {
    this.panelState.update(current => ({ ...current, overlayPosition }));
  }

  private updateDialDestination(destination: string) {
    this.dialerState.update(current => ({ ...current, destination }));
  }

  private resetOriginState() {
    this.originPreferenceRequestVersion++;
    this.callerIdPreviewRequestVersion++;
    this.dialerState.update(current => ({
      ...current,
      originQuery: '',
      originMode: 'local-presence',
      selectedOriginNumberId: undefined,
      originPreference: undefined,
      originNumberOptions: [],
      callerIdPreview: undefined,
      loadingOriginNumberOptions: false,
      savingOriginPreference: false,
      loadingCallerIdPreview: false,
    }));
  }

  private beginOriginPreview() {
    this.dialerState.update(current => ({ ...current, loadingCallerIdPreview: true }));
  }

  private finishOriginPreview(callerIdPreview?: CallerIdPreview) {
    this.dialerState.update(current => ({ ...current, callerIdPreview, loadingCallerIdPreview: false }));
  }

  private clearOriginPreview() {
    this.callerIdPreviewRequestVersion++;
    this.finishOriginPreview();
  }

  private beginOutgoingPreparation() {
    this.dialerState.update(current => ({ ...current, preparingOutgoing: true }));
  }

  private finishOutgoingPreparation() {
    this.dialerState.update(current => ({ ...current, preparingOutgoing: false }));
  }

  private resetDialer() {
    this.originPreferenceRequestVersion++;
    this.callerIdPreviewRequestVersion++;
    this.dialerState.set({
      destination: '',
      originQuery: '',
      originMode: 'local-presence',
      originNumberOptions: [],
      loadingOriginNumberOptions: false,
      savingOriginPreference: false,
      loadingCallerIdPreview: false,
      resolvingFromNumber: false,
      preparingOutgoing: false,
    });
  }

  private summarizeCall(
    call: TwilioCallLike | undefined,
    progress: SoftphoneCallProgress,
    targetNumber?: string,
  ): SoftphoneSessionActiveCallSummary {
    const transferContext = this.callState().transferContext;
    return {
      callId: this.callState().voxrCallId || this.getCallParam(call, 'CallId') || transferContext?.call?._id?.toString(),
      callSid: this.getCallSid(call),
      transferId: transferContext?.incomingType === 'recovery' ? undefined
        : this.getCallParam(call, 'TransferId') || transferContext?.transfer?._id?.toString(),
      integrationId: this.getCallParam(call, 'IntegrationId')
        || transferContext?.transfer?.integrationId?.toString()
        || transferContext?.call?.integrationId?.toString()
        || this.softphoneService.selectedIntegrationId(),
      targetNumber: targetNumber || this.callState().leadContext?.leadPhone || this.dialerState().destination,
      startedAt: this.callState().mainStartedAt || Date.now(),
      source: this.callState().leadContext?.source || (this.callState().activeTransfer ? 'campaign-tasks' : 'manual'),
      progress,
    };
  }

  private beginOutgoingCall(targetNumber: string, call?: TwilioCallLike) {
    if (call) this.activeCall.set(call);
    const active = this.summarizeCall(call, 'dialing', targetNumber);
    this.callState.update(current => ({
      ...current,
      active,
      progress: 'dialing',
    }));
  }

  private beginIncomingCall(call: TwilioCallLike) {
    this.incomingCall.set(call);
    const transferContext = this.callState().transferContext;
    this.callState.update(current => ({
      ...current,
      incoming: {
        handoff: transferContext?.handoff,
        call: transferContext?.call,
        campaign: transferContext?.campaign,
        transfer: transferContext?.transfer,
      },
      progress: 'ringing',
    }));
  }

  private connectCall(call: TwilioCallLike, incoming: boolean) {
    this.activeCall.set(call);
    if (incoming && this.incomingCall() === call) this.incomingCall.set(undefined);
    const active = this.summarizeCall(call, 'connected');
    this.callState.update(current => ({
      ...current,
      active,
      incoming: incoming ? undefined : current.incoming,
      progress: 'connected',
    }));
  }

  private setCallProgress(progress: SoftphoneCallProgress) {
    this.callState.update(current => ({
      ...current,
      active: current.active ? { ...current.active, progress } : current.active,
      progress,
    }));
  }

  private setActiveVoxrCallId(voxrCallId?: string) {
    this.callState.update(current => ({
      ...current,
      active: current.active ? { ...current.active, callId: voxrCallId } : current.active,
      voxrCallId,
    }));
  }

  private finishCall(call: TwilioCallLike) {
    const wasActive = this.activeCall() === call;
    const wasIncoming = this.incomingCall() === call;
    if (wasActive) this.activeCall.set(undefined);
    if (wasIncoming) this.incomingCall.set(undefined);
    this.callState.update(current => ({
      ...current,
      active: wasActive ? undefined : current.active,
      incoming: wasIncoming ? undefined : current.incoming,
      progress: wasActive || wasIncoming ? 'ended' : current.progress,
    }));
  }

  private failOutgoingCallStart() {
    const call = this.activeCall();
    if (call) {
      try { call.disconnect(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'call-disconnect-cleanup' }); }
      this.activeCall.set(undefined);
    }
    this.callState.update(current => ({
      ...current,
      active: undefined,
      progress: 'idle',
      voxrCallId: undefined,
      callerIdMessage: undefined,
    }));
  }

  private acceptMirroredCall(active: SoftphoneSessionActiveCallSummary) {
    this.callState.update(current => ({
      ...current,
      mirroredIncoming: undefined,
      incomingNotice: undefined,
      remoteActive: active,
      progress: active.progress || 'connected',
    }));
  }

  private resetCallState() {
    this.cancelIncomingAutoAnswer();
    this.clearIncomingFallback();
    this.activeCall.set(undefined);
    this.incomingCall.set(undefined);
    this.finishOutgoingPreparation();
    this.stopUiTimer();
    this.callState.set({
      progress: 'idle',
      muted: false,
      onHold: false,
      mainDurationSeconds: 0,
      childLegDurationSeconds: 0,
      threeWayAccepted: false,
    });
  }

  constructor() {
    toObservable(this.threeWayReadinessReason).pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe(reason => {
      this.log('three-way:readiness', {
        actor: 'agent',
        reason,
        ready: reason === 'ready',
        transferMode: this.activeTransferMode(),
        callProgress: this.callProgress(),
        reconnecting: this.threeWayConnectionRecovering(),
        hasTransferContext: !!this.getActiveTransferId(),
        callIdAvailable: !!this.getActiveCallIdForParticipantOps(),
        callSidAvailable: !!this.getCallSid(this.activeCall()),
        mutationPending: this.conferenceMutationPending(),
      });
    });
    this.threeWayNumber.set(localStorage.getItem(this.threeWayNumberStorageKey) || '');
    this.panelState.update(current => ({
      ...current,
      outboundSettingsCollapsed: this.getStoredBoolean(this.outboundSettingsCollapsedStorageKey, false),
    }));
    const storedPosition = this.getStoredOverlayPosition();
    if (storedPosition) this.setOverlayPosition(this.clampOverlayPosition(storedPosition));

    combineLatest([
      toObservable(this.selectedIntegrationId),
      toObservable(this.canOriginPreferences),
    ]).pipe(
      takeUntilDestroyed(),
      distinctUntilChanged(([prevIntegrationId, prevIsWorkspaceRoute], [nextIntegrationId, nextIsWorkspaceRoute]) =>
        prevIntegrationId === nextIntegrationId && prevIsWorkspaceRoute === nextIsWorkspaceRoute
      ),
      switchMap(([selectedIntegrationId, canOriginPreferences]) => {
        if (!selectedIntegrationId || !canOriginPreferences) {
          return of({ reset: true } as const);
        }

        return from(this.loadOriginPreference(selectedIntegrationId)).pipe(
          map(() => ({ reset: false } as const))
        );
      })
    ).subscribe(result => {
      if (!result.reset) return;
      this.resetOriginState();
    });

    combineLatest([
      toObservable(this.canOriginPreferences),
      toObservable(this.selectedIntegrationId),
      toObservable(this.callerIdPreviewTarget),
      toObservable(computed(() => this.dialerState().originMode)),
      toObservable(computed(() => this.dialerState().selectedOriginNumberId)),
      toObservable(this.hasAnyCall),
      toObservable(this.previewBlocked),
    ]).pipe(
      takeUntilDestroyed(),
      distinctUntilChanged((prev, next) =>
        prev[0] === next[0]
        && prev[1] === next[1]
        && prev[2] === next[2]
        && prev[3] === next[3]
        && prev[4] === next[4]
        && prev[5] === next[5]
        && prev[6] === next[6]
      ),
      switchMap(([isWorkspaceRoute, selectedIntegrationId, previewTarget, selectedOriginMode, _selectedOriginNumberId, hasAnyCall, outboundBlocked]) => {
        if (!isWorkspaceRoute || !selectedIntegrationId || !previewTarget || hasAnyCall || selectedOriginMode !== 'specific-number' || outboundBlocked) {
          return of({ reset: true } as const);
        }

        return from(this.refreshCallerIdPreview(previewTarget, selectedIntegrationId)).pipe(
          map(() => ({ reset: false } as const))
        );
      })
    ).subscribe(result => {
      if (result.reset) this.clearOriginPreview();
    });

    this.softphoneService.softphoneState$.pipe(takeUntilDestroyed()).subscribe(state => {
      this.transitionRuntimeConnection(state);
    });
    this.app.navigationEnd$.pipe(takeUntilDestroyed()).subscribe(e => {
      this.routeArea.set(e.data?.['area']);
    });
    this.workspaceService.workspace$.pipe(takeUntilDestroyed()).subscribe(workspace => {
      this.currentWorkspace.set(workspace);
    });
    this.softphoneService.softphoneStateDetail$.pipe(takeUntilDestroyed()).subscribe(detail => {
      this.updateRuntimeDetail(detail);
    });
    this.softphoneService.browserSessionMode$.pipe(takeUntilDestroyed()).subscribe(mode => {
      this.updateBrowserSession(mode);
      if (mode !== 'active') {
        this.cancelIncomingAutoAnswer();
        this.clearIncomingFallback();
      }
      if (mode === 'active' || mode === 'disabled' || mode === 'unverified') this.browserSessionDialogRef?.close();
    });
    this.softphoneService.activeBrowser$.pipe(takeUntilDestroyed()).subscribe(activeBrowser => {
      this.updateBrowserSession(this.browserSessionMode(), activeBrowser);
    });
    this.softphoneService.browserSessionConflictRequested$.pipe(takeUntilDestroyed()).subscribe(response => {
      if (!response) {
        this.browserSessionDialogRef?.close();
        return;
      }
      setTimeout(() => this.openBrowserSessionConflict(response));
    });
    this.softphoneService.browserSessionOwnershipLost$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.releaseLiveQualityTracking();
      this.loseBrowserSession();
    });
    this.softphoneService.selectedDevice$.pipe(takeUntilDestroyed()).subscribe(device => {
      if (this.device === device) return;
      this.device = device;
      if (this.device && (this.device as any)?.state === 'registered') void this.onDeviceRegistered(this.device);
      if (!this.device) this.registeredDevice = undefined;
    });
    this.softphoneService.deviceEvent$.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event.device !== this.device) return;
      if (event.kind === 'registered') void this.onDeviceRegistered(event.device);
      else if (event.kind === 'unregistered') void this.onDeviceUnregistered(event.device);
      else void this.onDeviceError(event.error, event.device);
    });
    this.softphoneService.sessionState$.pipe(takeUntilDestroyed()).subscribe(sessionState => {
      if (!sessionState.isOwner) {
        this.cancelIncomingAutoAnswer();
        this.releaseLiveQualityTracking();
        this.clearIncomingFallback();
      }
      this.applyMirroredRuntime(sessionState);
      this.applyMirroredIncomingTransfer(sessionState.incomingTransfer);
      if (!this.softphoneService.isOwnerTab
        && sessionState.activeCall
        && this.matchesPendingRemoteAcceptedCall(sessionState.activeCall)) {
        const activeCall = sessionState.activeCall;
        this.clearPendingRemoteAcceptedCall();
        this.acceptMirroredCall(activeCall);
        this.expandPanel();
        this.openPanel();
      } else if (!sessionState.activeCall) {
        this.callState.update(current => ({
          ...current,
          remoteActive: undefined,
          progress: current.active || current.incoming ? current.progress : 'idle',
        }));
      }
      if (sessionState.activeCall?.callId
        && this.pendingOutboundOwnerCommand?.callId === sessionState.activeCall.callId) {
        this.clearPendingOutboundOwnerCommand();
        this.finishOutgoingPreparation();
        this.restoreMirroredSoftphoneState();
      }
    });

    this.softphoneService.ownerCommand$.pipe(takeUntilDestroyed()).subscribe(command => {
      void this.handleOwnerCommand(command);
    });
    this.softphoneService.ownerCommandResult$.pipe(takeUntilDestroyed()).subscribe(result => {
      if (result.commandType === 'verify-browser-session' && result.payload?.browserSession) {
        this.softphoneService.presentBrowserSessionConflict(result.payload.browserSession);
        return;
      }
      if (result.commandType === 'start-outbound-call') {
        if (!this.pendingOutboundOwnerCommand) return;
        if (result.messageId !== this.pendingOutboundOwnerCommand.messageId) return;
        if (result.createdAt > this.pendingOutboundOwnerCommand.deadlineAt || Date.now() > this.pendingOutboundOwnerCommand.deadlineAt) return;
        if (result.payload?.outboundPhase === 'connecting') {
          this.pendingOutboundOwnerCommand.callId = result.payload.callId || this.pendingOutboundOwnerCommand.callId;
          return;
        }
        this.clearPendingOutboundOwnerCommand();
        this.finishOutgoingPreparation();
        this.restoreMirroredSoftphoneState();
        if (!result.ok) {
          this.app.message$.next({
            severity: 'danger',
            summary: 'Soft phone',
            detail: result.error || 'Soft phone action failed in owner tab.',
          });
        }
        return;
      }
      if (result.ok) {
        this.finishOutgoingPreparation();
        return;
      }
      if (result.commandType === 'accept-incoming-transfer') {
        this.clearPendingRemoteAcceptedCall();
      }
      this.finishOutgoingPreparation();
      this.app.message$.next({
        severity: 'danger',
        summary: 'Soft phone',
        detail: result.error || 'Soft phone action failed in owner tab.',
      });
    });
    this.softphoneService.incomingTransferTabFocusRequested$.pipe(takeUntilDestroyed()).subscribe(command => {
      const incomingTransfer = this.mirroredIncomingTransfer();
      const transferId = incomingTransfer?.transfer?._id?.toString()
        || this.ownerIncomingTransferContext?.transfer?._id?.toString()
        || this.getCallParam(this.incomingCall() || this.ownerIncomingCall, 'TransferId');
      if (command.transferId && command.transferId !== transferId) return;
      this.expandPanel();
      this.openPanel();
      window.focus();
    });
    this.softphoneService.softphoneCallLeadRequested$.pipe(takeUntilDestroyed()).subscribe(intent => {
      this.prepareLeadCall(intent);
    });
    this.userService.softphoneStateDto$.pipe(takeUntilDestroyed()).subscribe(state => {
      this.runtimeState.update(current => ({ ...current, config: state }));
    });

    this.softphoneService.softphonePanelOpenRequested$.pipe(takeUntilDestroyed()).subscribe(request => {
      if (request.source === 'call-lead' || request.source === 'incoming-transfer') {
        this.expandPanel();
      }
      this.openPanel();
    });

    this.softphoneService.ownerIncomingCall$.pipe(takeUntilDestroyed()).subscribe(({ call, integrationId, device }) => {
      if (this.ownerIncomingCall === call || this.incomingCall() === call) return;
      if (!this.softphoneService.isOwnerTab) return;
      this.logIncomingTransferTwilioReceived(call, 'managed-runtime', integrationId);
      this.clearIncomingFallback();
      this.ownerIncomingCall = call;
      this.cancelIncomingAutoAnswer();
      this.incomingAutoAnswer = { call, mode: this.autoAnswerMode(), userId: this.user()?._id?.toString() };
      this.setCallProgress('ringing');
      this.attachCallListeners(call, true, undefined, device);
      this.resolveAndSetCallId(call);
      this.softphoneService.publishSoftphoneState(this.state(), this.transferContext() ? 'Incoming transfer call' : 'Incoming call');
      this.reconcileOwnerIncomingTransfer();
      if (!this.ownerIncomingTransferContext?.targetTabId) this.scheduleIncomingFallback(call);
      void this.tryAutoAnswerIncoming();
    });

    this.callService.incomingCall$.pipe(takeUntilDestroyed()).subscribe(payload => {
      this.softphoneService.receiveQualityTelemetry(payload.softphoneQualityTelemetry);
      if (!this.softphoneService.isOwnerTab) return;
      const workspaceId = this.getIncomingPayloadWorkspaceId(payload);
      const shouldAdoptPayload = this.shouldAdoptIncomingTransferPayload(payload);
      this.log('incoming-transfer:context-received', {
        type: payload.type,
        transferId: payload.transfer?._id?.toString(),
        callId: payload.call?._id?.toString(),
        integrationId: payload.call?.integrationId?.toString(),
        workspaceId,
        isOwner: this.softphoneService.isOwnerTab,
        adopted: shouldAdoptPayload,
        callProgress: this.callProgress(),
      });
      if (!shouldAdoptPayload) return;
      const tabs = this.softphoneService.selectIncomingTransferTabs(workspaceId);
      this.captureOwnerIncomingTransferContext(payload, workspaceId, tabs.targetTabId, tabs.noticeTabId);
      this.setTransferContextFromIncomingPayload(payload);
      this.reconcileOwnerIncomingTransfer();
    });

    this.userService.user$.pipe(takeUntilDestroyed()).subscribe(async user => {
      if (this.qualityUserId !== user?._id?.toString()) {
        this.cancelAutomaticReport();
        for (const entry of this.qualityCalls.values()) entry.cleanup();
        this.qualityCalls.clear(); this.getDiagnostics().resetQuality(); this.clearCallQuality();
        this.qualityUserId = user?._id?.toString();
      }
      this.user.set(user || undefined);
      if (!this.enabled()) {
        await this.destroyDevice(false);
        this.softphoneService.publishSoftphoneState('inactive', 'Soft phone is off');
      }
    });

    this.socketService.onEvent$<SoftphoneParticipantSnapshotDto & { callId?: string }>('softphone-participants-updated')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        this.reconcileParticipantSnapshot(payload);
      });

    this.socketService.onEvent$<SoftphoneParticipantSnapshotDto & { callId?: string; participantCallSid?: string; action?: string }>('softphone-participant-action')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        this.reconcileParticipantSnapshot(payload);
        const agentCallSid = this.activeCall()?.parameters['CallSid'];
        if (agentCallSid && payload?.participantCallSid === agentCallSid) {
          if (payload.action === 'hold') this.callState.update(current => ({ ...current, onHold: true }));
          else if (payload.action === 'unhold') this.callState.update(current => ({ ...current, onHold: false }));
          return;
        }
      });

    this.socketService.onEvent$<{ callId?: string; isOnHold?: boolean; source?: string; participantCallSid?: string }>('softphone-hold-state')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        if (typeof payload?.isOnHold !== 'boolean') return;

        const agentCallSid = this.getCallSid(this.activeCall());
        if (payload.participantCallSid && agentCallSid && payload.participantCallSid !== agentCallSid) return;

        this.callState.update(current => ({ ...current, onHold: !!payload.isOnHold }));
      });

    this.socketService.onEvent$<SoftphoneParticipantSnapshotDto & { callId?: string }>('softphone-participant-ended')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        this.reconcileParticipantSnapshot(payload);
      });

    this.socketService.onEvent$<SoftphoneParticipantSnapshotDto & { callId?: string }>('softphone-conference-ended')
      .pipe(takeUntilDestroyed())
      .subscribe(payload => {
        const activeCallId = this.getActiveCallIdForParticipantOps();
        if (!activeCallId || payload?.callId !== activeCallId) return;
        this.conferenceSession.reset();
        this.callState.update(current => ({ ...current, childLegStartedAt: undefined }));
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnloadProtectedCallState()) {
      event.preventDefault();
      // Modern browsers ignore custom beforeunload text and show a generic warning.
      event.returnValue = '';
      return;
    }
    if (this.enabled()) void this.softphoneService.publishOwnerRuntimeSnapshot('shutdown', { activeCall: null, enabled: this.enabled() });
  }

  private async handleOwnerCommand(command: SoftphoneOwnerCommand) {
    if (!this.softphoneService.isOwnerTab) return;
    try {
      switch (command.type) {
        case 'capture-call-quality':
          this.refreshQualityContexts();
          this.softphoneService.sendOwnerCommandResult(command, true, { callQuality: this.getDiagnostics().qualitySnapshot('owner-response') }); return;
        case 'cleanup-call-quality':
          this.getDiagnostics().acknowledgeCaptures(command.payload.captureIds.filter(id => typeof id === 'string').slice(0, 2)); return;
        case 'check-setup':
          this.softphoneService.sendOwnerCommandResult(command, await this.softphoneService.checkSetup());
          break;
        case 'reconnect':
          await this.softphoneService.recoverSoftphoneDevice('softphone', {
            ensureMicrophone: command.payload?.ensureMicrophone,
            forceRecreate: command.payload?.forceRecreate,
            integrationId: command.payload?.integrationId,
          });
          this.softphoneService.sendOwnerCommandResult(command, true);
          break;
        case 'accept-incoming-transfer':
          if (!this.incomingCall() && !this.ownerIncomingCall) {
            throw new Error('Transfer is still connecting. Please try again in a moment.');
          }
          this.acceptIncoming();
          this.softphoneService.sendOwnerCommandResult(command, true);
          break;
        case 'reject-incoming-transfer':
          if (!this.incomingCall() && !this.ownerIncomingCall) {
            throw new Error('Transfer is still connecting. Please try again in a moment.');
          }
          this.rejectIncoming();
          this.softphoneService.sendOwnerCommandResult(command, true);
          break;
        case 'focus-active-call':
          if (!this.activeCall()) {
            throw new Error('The active call is no longer available in the owner tab.');
          }
          this.expandPanel();
          this.openPanel();
          window.focus();
          this.softphoneService.sendOwnerCommandResult(command, true);
          break;
        case 'hang-up':
          if (command.payload.callId !== this.getActiveCallIdForParticipantOps()) {
            throw new Error('The requested call is no longer active. Refresh the soft phone and try again.');
          }
          this.endCall();
          this.softphoneService.sendOwnerCommandResult(command, true);
          break;
        case 'switch-browser-session':
          await this.softphoneService.switchToThisBrowser();
          this.softphoneService.sendOwnerCommandResult(command, true);
          break;
        case 'verify-browser-session': {
          const browserSession = await this.softphoneService.verifyBrowserSessionForForwardedAction();
          this.softphoneService.sendOwnerCommandResult(command, true, { browserSession });
          break;
        }
        case 'focus-owner-tab':
          this.expandPanel();
          this.openPanel();
          window.focus();
          this.showOwnerFocusNotification();
          this.softphoneService.sendOwnerCommandResult(command, true);
          break;
        case 'release-ownership':
          if (!this.softphoneService.canReleaseTabOwnershipForRecovery(command)) {
            this.softphoneService.sendOwnerCommandResult(
              command,
              false,
              undefined,
              'The owner tab is active again, so recovery was cancelled.',
            );
            return;
          }
          if (this.hasAnyCall()) {
            throw new Error('The owner tab has an active call and cannot release the soft phone.');
          }
          await this.softphoneService.releaseTabOwnershipForRecovery();
          this.softphoneService.sendOwnerCommandResult(command, true);
          break;
        case 'start-outbound-call':
          if (command.payload?.dialNumber) {
            this.updateDialDestination(command.payload.dialNumber);
            this.fromNumberResolutionPromise = undefined;
            this.callState.update(current => ({ ...current, leadContext: command.payload.callLeadContext }));
          }
          if (command.payload?.integrationId) {
            this.softphoneService.setSelectedIntegrationId(command.payload.integrationId);
          }
          const result = await this.startCall({
            deadlineAt: command.createdAt + this.outboundConnectTimeoutMs,
            workspaceId: command.payload.workspaceId,
            onProviderConnectStart: callId => {
              this.softphoneService.sendOwnerCommandResult(command, true, { outboundPhase: 'connecting', callId });
            },
          });
          if (!result) throw new Error('Soft phone command failed.');
          this.softphoneService.sendOwnerCommandResult(command, result.ok, undefined, result.ok ? undefined : result.error);
          return;
      }
    } catch (error: any) {
      if (!safeOwnerCommandErrors.has(error?.message)) this.exceptions.record(error, { source: 'softphone', operation: 'owner-command' });
      const message = this.getFriendlyActionError(error, 'command');
      this.softphoneService.sendOwnerCommandResult(command, false, undefined, message);
      this.app.message$.next({
        severity: 'danger',
        summary: 'Soft phone',
        detail: message,
      });
    }
  }

  private showOwnerFocusNotification() {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    try {
      this.ownerFocusNotification?.close();
      const notification = new Notification('VOXR active softphone', {
        body: 'Click to open the tab that controls your softphone.',
        tag: 'voxr-active-softphone',
      });
      this.ownerFocusNotification = notification;
      notification.onclick = () => {
        this.expandPanel();
        this.openPanel();
        window.focus();
        notification.close();
        if (this.ownerFocusNotification === notification) this.ownerFocusNotification = undefined;
      };
    } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'owner-focus-notification' }); }
  }

  ngOnDestroy() {
    this.cancelAutomaticReport();
    for (const entry of this.qualityCalls.values()) entry.cleanup();
    this.qualityCalls.clear();
    this.clearCallQuality();
    this.ownerFocusNotification?.close();
    this.ownerFocusNotification = undefined;
    this.conferenceSession.dispose();
    this.resetDialer();
    this.resetCallState();
    this.clearPendingRemoteAcceptedCall();
    this.clearPendingOutboundOwnerCommand();
    this.clearMirroredIncomingTransferTimeout();
    this.connectionDetailsDialogRef?.close();
    this.browserSessionDialogRef?.close();
    this.tabControlDialogRef?.close();
    this.overlayRef?.dispose();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      void this.reacquireWakeLock();
      // Reconnect if the tab was backgrounded and missed the unregistered event or reconnect failed
      if (this.enabled() && this.browserSessionMode() === 'active' && this.state() === 'inactive' && !this.runtimeState().initializing) {
        this.log('visibility:reconnect', { visibilityState: document.visibilityState });
        void this.initializeSoftphone(true);
      }
    }
  }

  openPanel(event?: MouseEvent) {
    const openedFromSoftphoneButton = !!event?.currentTarget;
    if (openedFromSoftphoneButton && this.browserSessionMode() !== 'active' && this.browserSessionMode() !== 'disabled') {
      void this.softphoneService.verifyBrowserSessionForAction();
    }
    if (openedFromSoftphoneButton && !this.hasAnyCall()) {
      // Manual softphone opens should start a fresh direct-call context.
      this.callState.update(current => ({ ...current, leadContext: undefined }));
      this.fromNumberResolutionPromise = undefined;
    }

    const wasOpen = this.panelState().open;
    if (!wasOpen) {
      const openerFromEvent = event?.currentTarget as HTMLElement | null | undefined;
      this.ensureOverlayRef();
      const storedPosition = this.getStoredOverlayPosition();
      const position = storedPosition || this.getOverlayPositionNearOpener(openerFromEvent || undefined);
      this.applyOverlayPosition(position || this.panelState().overlayPosition, true);
      this.expandPanel();
    } else if (this.hasIncomingTransfer() || this.callLeadContext()) {
      this.expandPanel();
    }

    this.ensureOverlayAttached();
    this.setPanelOpen(true);
    this.applyOverlayPosition(this.panelState().overlayPosition, false);

    void this.resolveFromNumberForCurrentContext();
  }

  private openBrowserSessionConflict(_response: SoftphoneBrowserSessionResponseDto) {
    if (this.browserSessionDialogRef) return;
    this.browserSessionDialogRef = this.dialog.open(this.browserSessionDialog, {
      width: '440px',
      maxWidth: '95vw',
      disableClose: true,
    });
    this.browserSessionDialogRef.afterClosed().subscribe(() => {
      this.browserSessionDialogRef = undefined;
    });
  }

  stayOnOtherBrowser() {
    this.softphoneService.stayOnOtherBrowser();
    this.browserSessionDialogRef?.close();
  }

  retryBrowserSession() {
    void this.softphoneService.verifyBrowserSessionForAction();
  }

  retryOwnerRecovery() {
    this.softphoneService.retryOwnerRecovery();
  }

  findOwnerTab() {
    this.softphoneService.focusOwnerTab();
  }

  async releaseSoftphoneOwnership() {
    if (this.hasAnyCall()) {
      this.app.message$.next({
        severity: 'warning',
        summary: 'Soft phone is busy',
        detail: 'End the active call before releasing the soft phone to another tab.',
      });
      return;
    }
    await this.softphoneService.releaseTabOwnershipForRecovery();
  }

  async switchToThisBrowser() {
    if (this.switchingBrowserSession()) return;
    this.beginBrowserSwitch();
    try {
      const response = await this.softphoneService.switchToThisBrowser();
      if (response.mode === 'active') {
        this.browserSessionDialogRef?.close();
        return;
      }
      if (response.mode === 'blocked') {
        this.app.message$.next({
          severity: 'warning',
          summary: 'Soft phone is busy',
          detail: 'End the active softphone call before switching browsers.',
        });
      }
    } catch (error: any) {
      const blockedByCall = error?.error?.code === 'SOFTPHONE_BROWSER_TAKEOVER_BLOCKED_BY_CALL';
      if (!blockedByCall) this.exceptions.record(error, { source: 'softphone', operation: 'browser-switch' });
      this.app.message$.next({
        severity: blockedByCall ? 'warning' : 'danger',
        summary: blockedByCall ? 'Soft phone is busy' : 'Could not switch soft phone',
        detail: blockedByCall
          ? 'End the active softphone call before switching browsers.'
          : this.getFriendlyActionError(error, 'command'),
      });
    } finally {
      this.finishBrowserSwitch();
    }
  }

  browserSessionLastSeenText() {
    const lastSeenAt = this.activeBrowserSession()?.lastSeenAt;
    if (!lastSeenAt) return 'recently';
    const value = new Date(lastSeenAt);
    return Number.isNaN(value.getTime()) ? 'recently' : value.toLocaleString();
  }

  private loseBrowserSession() {
    this.releaseWakeLock();
    this.ownerIncomingCall = undefined;
    this.clearOwnerIncomingTransferMirrorState();
    this.clearOwnerPublishedActiveCallMirrorState();
    this.clearActiveTransfer('browser-session-lost');
    this.clearTransferContext('browser-session-lost');
    this.resetCallState();
    this.registeredDevice = undefined;
    this.device = undefined;
  }

  @HostListener('window:offline')
  onOffline() {
    if (!this.enabled()) return;
    const hasProtectedCall = this.hasUnloadProtectedCallState() || !!this.device?.isBusy;
    this.log('network:offline', {
      activeCallSid: this.getCallSid(this.activeCall()),
      callProgress: this.callProgress(),
      hasProtectedCall,
      visibilityState: document.visibilityState,
    });
    if (hasProtectedCall) {
      this.softphoneService.publishSoftphoneState('loading', 'Call connection interrupted. Reconnecting...');
      return;
    }
    this.softphoneService.publishSoftphoneState('inactive', 'Offline');
    void this.destroyDevice(true);
  }

  @HostListener('window:online')
  onOnline() {
    if (!this.enabled() || this.browserSessionMode() !== 'active') return;
    if (this.device || this.runtimeState().initializing) {
      if (this.hasUnloadProtectedCallState() || this.device?.isBusy) void this.reacquireWakeLock();
      return;
    }
    this.log('network:online:reconnect');
    void this.initializeSoftphone(true);
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.applyOverlayPosition(this.panelState().overlayPosition, true);
  }

  onPanelDragEnded(event: CdkDragEnd) {
    const currentPosition = this.panelState().overlayPosition;
    const nextPosition = this.clampOverlayPosition({
      left: currentPosition.left + event.distance.x,
      top: currentPosition.top + event.distance.y,
    });
    event.source.reset();
    this.applyOverlayPosition(nextPosition, true);
  }

  togglePanelCollapsed() {
    this.panelState.update(current => ({ ...current, collapsed: !current.collapsed }));
  }

  toggleDtmfCardExpanded() {
    this.panelState.update(current => ({ ...current, dtmfExpanded: !current.dtmfExpanded }));
  }

  toggleOutboundSettingsCollapsed() {
    const nextValue = !this.panelState().outboundSettingsCollapsed;
    this.panelState.update(current => ({ ...current, outboundSettingsCollapsed: nextValue }));
    this.setStoredBoolean(this.outboundSettingsCollapsedStorageKey, nextValue);
  }

  private getOverlayPositionNearOpener(preferredOpener?: HTMLElement) {
    const opener = preferredOpener || this.softphoneOpener?.nativeElement;
    if (!opener) return undefined;

    const rect = opener.getBoundingClientRect();
    const overlayWidth = this.getOverlayWidthForViewport();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || overlayWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    let left = rect.left;
    if (left + overlayWidth + this.overlayMargin > viewportWidth) {
      left = viewportWidth - overlayWidth - this.overlayMargin;
    }
    left = Math.max(this.overlayMargin, left);

    let top = rect.bottom + 8;
    if (viewportHeight > 0 && top + this.overlayEstimatedHeight > viewportHeight - this.overlayMargin) {
      top = Math.max(this.overlayMargin, rect.top - this.overlayEstimatedHeight - 8);
    }

    return {
      left: Math.round(left),
      top: Math.round(top),
    };
  }

  private getStoredOverlayPosition() {
    try {
      const rawValue = localStorage.getItem(this.overlayPositionStorageKey);
      if (!rawValue) return undefined;
      const position = JSON.parse(rawValue) as { left?: unknown; top?: unknown };
      if (typeof position.left !== 'number' || typeof position.top !== 'number') return undefined;
      return { left: position.left, top: position.top };
    } catch (error) {
      this.exceptions.record(error, { source: 'softphone', operation: 'read-overlay-position' });
      return undefined;
    }
  }

  private storeOverlayPosition(position: { left: number; top: number }) {
    try {
      localStorage.setItem(this.overlayPositionStorageKey, JSON.stringify({
        left: Math.round(position.left),
        top: Math.round(position.top),
      }));
    } catch (error) {
      this.exceptions.record(error, { source: 'softphone', operation: 'write-overlay-position' });
    }
  }

  private clampOverlayPosition(position: { left: number; top: number }, width = this.getOverlayWidthForViewport(), height = this.overlayEstimatedHeight) {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || width;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || height;
    const effectiveWidth = Math.min(width, Math.max(0, viewportWidth - this.overlayMargin * 2));
    const effectiveHeight = Math.min(height, Math.max(0, viewportHeight - this.overlayMargin * 2));
    const maxLeft = Math.max(this.overlayMargin, viewportWidth - effectiveWidth - this.overlayMargin);
    const maxTop = Math.max(this.overlayMargin, viewportHeight - effectiveHeight - this.overlayMargin);

    return {
      left: Math.round(Math.min(Math.max(this.overlayMargin, position.left), maxLeft)),
      top: Math.round(Math.min(Math.max(this.overlayMargin, position.top), maxTop)),
    };
  }

  private ensureOverlayAttached() {
    const overlayRef = this.ensureOverlayRef();
    if (!this.overlayPortal) {
      this.overlayPortal = new TemplatePortal(this.softphoneOverlayContent, this.viewContainerRef);
    }
    if (!overlayRef.hasAttached()) {
      overlayRef.attach(this.overlayPortal);
    }
  }

  private ensureOverlayRef() {
    if (this.overlayRef) return this.overlayRef;

    this.overlayPositionStrategy = this.overlay.position().global();
    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      disposeOnNavigation: false,
      positionStrategy: this.overlayPositionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: `${this.getOverlayWidthForViewport()}px`,
    });

    return this.overlayRef;
  }

  private applyOverlayPosition(position: { left: number; top: number }, persist: boolean) {
    const { width, height } = this.getOverlayDimensions();
    const nextPosition = this.clampOverlayPosition(position, width, height);

    this.setOverlayPosition(nextPosition);
    if (persist) this.storeOverlayPosition(nextPosition);

    if (!this.overlayRef || !this.overlayPositionStrategy) return;

    const overlayWidth = this.getOverlayWidthForViewport();
    this.overlayRef.updateSize({ width: `${overlayWidth}px` });
    this.overlayPositionStrategy.left(`${nextPosition.left}px`).top(`${nextPosition.top}px`);
    this.overlayRef.updatePosition();
  }

  private getOverlayDimensions() {
    const overlayWidth = this.getOverlayWidthForViewport();
    if (!this.overlayRef?.hasAttached()) {
      return { width: overlayWidth, height: this.overlayEstimatedHeight };
    }

    const rect = this.overlayRef.overlayElement.getBoundingClientRect();
    return {
      width: rect.width || overlayWidth,
      height: rect.height || this.overlayEstimatedHeight,
    };
  }

  private getOverlayWidthForViewport() {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || this.overlayWidth;
    return Math.min(this.overlayWidth, Math.max(0, viewportWidth - this.overlayMargin * 2));
  }

  closePanel() {
    if (this.hasAnyCall()) return;
    this.connectionDetailsDialogRef?.close();
    this.overlayRef?.detach();
    this.setPanelOpen(false);
  }

  appendDigit(digit: string) {
    if (this.activeCall()) {
      this.activeCall()?.sendDigits(digit);
      return;
    }

    const nextDialNumber = `${this.dialerState().destination}${digit}`;
    this.updateDialDestination(nextDialNumber);
  }

  clearDialer() {
    this.updateDialDestination('');
    this.callState.update(current => ({ ...current, leadContext: undefined }));
  }

  async checkSetup() {
    if (this.checkingSetup() || !this.setupIssue()) return;
    const userId = this.user()?._id?.toString();
    const workspaceId = this.currentWorkspace()?._id?.toString();
    const ownerInstanceId = this.sessionState().ownerInstanceId;
    const browserSessionId = this.activeBrowserSession()?.sessionId;
    this.checkingSetup.set(true);
    try {
      const ok = await new Promise<boolean>(resolve => {
        let subscription: { unsubscribe(): void } | undefined;
        const timer = setTimeout(() => { subscription?.unsubscribe(); resolve(false); }, 10_000);
        const finish = (result: boolean) => { clearTimeout(timer); subscription?.unsubscribe(); resolve(result); };
        try {
          if (this.softphoneService.isOwnerTab) {
            this.softphoneService.checkSetup().then(finish, () => finish(false));
          } else {
            const command = this.softphoneService.sendOwnerCommand({ type: 'check-setup', payload: {} });
            subscription = this.softphoneService.ownerCommandResult$.subscribe(result => {
              if (result.commandType !== 'check-setup' || result.messageId !== command.messageId
                || result.userId !== command.userId || result.targetTabId !== command.sourceTabId
                || result.ownerInstanceId !== command.targetOwnerInstanceId
                || result.sourceTabId !== this.sessionState().ownerTabId) return;
              finish(result.ok);
            });
          }
        } catch { finish(false); }
      }).catch(() => false);
      if (userId !== this.user()?._id?.toString() || workspaceId !== this.currentWorkspace()?._id?.toString()
        || ownerInstanceId !== this.sessionState().ownerInstanceId
        || browserSessionId !== this.activeBrowserSession()?.sessionId) return;
      if (!ok) this.app.message$.next({ severity: 'danger', summary: 'Soft phone',
        detail: 'Calling setup could not be checked. Please try again.' });
    } finally {
      this.checkingSetup.set(false);
    }
  }

  async reconnect() {
    this.log('manual:reconnect', { source: 'user' });
    if (this.softphoneService.isOwnerTab) {
      await this.initializeSoftphone(true);
      return;
    }

    await this.softphoneService.recoverSoftphoneDevice('softphone', {
      forceRecreate: true,
      ensureMicrophone: true,
    });
  }

  async reconnectFailed() {
    const failedConnectionIds = this.failedConnectionIds();
    this.log('manual:reconnect-failed', { source: 'user', failedConnectionIds });
    if (failedConnectionIds.length === 0) {
      await this.reconnect();
      return;
    }

    await this.reconnectConnections(failedConnectionIds);
  }

  async reconnectConnection(integrationId: string) {
    this.log('manual:reconnect-targeted', { source: 'user', integrationId });
    await this.reconnectConnections([integrationId]);
  }

  openTabControlDialog() {
    this.tabControlDialogRef?.close();
    this.tabControlDialogRef = this.dialog.open(this.tabControlDialog);
  }

  openConnectionDetailsDialog() {
    this.connectionDetailsDialogRef?.close();
    this.connectionDetailsDialogRef = this.dialog.open(this.connectionDetailsDialog, {
      autoFocus: false,
      restoreFocus: false,
    });
  }

  onWorkspacePhonesLoaded(phones: PhoneInterface[]) {
    this.workspacePhones = phones || [];
  }

  onSelectedIntegrationChange(integrationId: string) {
    this.softphoneService.setSelectedIntegrationId(integrationId || undefined);
  }

  onOriginNumberQueryChange(value: string) {
    this.dialerState.update(current => ({ ...current, originQuery: value }));
  }

  onSelectedOriginModeChange(originMode: SoftphoneOriginNumberMode) {
    this.dialerState.update(current => ({ ...current, originMode }));
  }

  onSelectedOriginNumberChange(selectedOriginNumberId?: string) {
    this.dialerState.update(current => ({ ...current, selectedOriginNumberId }));
  }

  async searchOriginNumbers() {
    const integrationId = this.selectedIntegrationId();
    if (!this.canOriginPreferences() || !integrationId || this.dialerState().originMode !== 'specific-number') return;

    await this.loadOriginNumberOptionsForIntegration(integrationId);
  }

  async saveOriginPreference() {
    const integrationId = this.selectedIntegrationId();
    if (!this.canOriginPreferences() || !integrationId) return;
    if (this.dialerState().originMode === 'specific-number' && !this.dialerState().selectedOriginNumberId) {
      this.app.message$.next({
        severity: 'danger',
        summary: 'Soft phone',
        detail: 'Select a pinned caller ID before saving a specific-number preference.',
      });
      return;
    }

    this.dialerState.update(current => ({ ...current, savingOriginPreference: true }));
    try {
      const preference = await this.softphoneService.saveSoftphoneOriginPreference({
        integrationId,
        mode: this.dialerState().originMode,
        phoneNumberId: this.dialerState().originMode === 'specific-number' ? this.dialerState().selectedOriginNumberId : undefined,
      });
      this.dialerState.update(current => ({ ...current, originPreference: preference }));
      if (preference.mode !== 'specific-number') {
        this.dialerState.update(current => ({ ...current, selectedOriginNumberId: undefined, callerIdPreview: undefined }));
      } else {
        await this.refreshCallerIdPreview();
      }
      this.app.message$.next({
        severity: 'success',
        summary: 'Soft phone',
        detail: 'Caller ID preference saved.',
      });
    } catch (error: any) {
      this.exceptions.record(error, { source: 'softphone', operation: 'caller-id-save' });
      this.app.message$.next({
        severity: 'danger',
        summary: 'Soft phone',
        detail: this.getFriendlyActionError(error, 'caller-id'),
      });
    } finally {
      this.dialerState.update(current => ({ ...current, savingOriginPreference: false }));
    }
  }

  async startCall(delegated?: {
    deadlineAt: number;
    workspaceId: string;
    onProviderConnectStart: (callId: string) => void;
  }): Promise<SoftphoneOutboundStartResult | undefined> {
    if (!this.canDial() || !this.dialerState().destination.trim() || this.hasAnyCall()) {
      return delegated ? { ok: false, error: 'Soft phone is not ready to place this call.' } : undefined;
    }
    if (!this.callReady()) {
      return delegated ? { ok: false, error: 'The selected soft phone integration is not connected.' } : undefined;
    }
    if (!this.softphoneService.isOwnerTab) {
      const workspaceId = this.workspaceService.workspaceId;
      if (!workspaceId) return { ok: false, error: 'Open a workspace before placing this call.' };
      const command = this.softphoneService.sendOwnerCommand({
        type: 'start-outbound-call',
        payload: {
          integrationId: this.softphoneService.selectedIntegrationId(),
          dialNumber: this.dialerState().destination,
          workspaceId,
          callLeadContext: this.callLeadContext(),
        },
      });
      this.beginOutgoingPreparation();
      this.softphoneService.publishSoftphoneState('loading', 'Starting call in owner tab...');
      this.startPendingOutboundOwnerCommand(command.messageId);
      return { ok: true };
    }
    if (!this.device) return delegated ? { ok: false, error: 'Soft phone runtime is not ready in the owner tab.' } : undefined;
    void this.reacquireWakeLock();

    if (delegated && Date.now() > delegated.deadlineAt) return { ok: false, error: this.outboundConnectTimeoutMessage };
    const delegatedPreviousState = delegated ? { state: this.state(), detail: this.stateDetail() } : undefined;
    const target = this.normalizeDialTarget(this.dialerState().destination);
    const callLeadContext = this.callLeadContext();
    const qualityDevice = this.device;
    const qualityId = this.getDiagnostics().startCapture({ sdkVersion: (qualityDevice.constructor as typeof Device).version, microphonePermission: this.softphoneService.microphonePermission,
      workspaceId: delegated?.workspaceId || this.workspaceService.workspaceId, integrationId: this.selectedIntegrationId(), campaignId: callLeadContext?.campaignId, direction: 'outgoing' });
    let preparedCall: SoftphonePrepareCallResponse | undefined;
    try {
      this.clearTransferContext('start-outbound-call');
      this.beginOutgoingPreparation();
      this.beginOutgoingCall(target);
      if (delegated && Date.now() > delegated.deadlineAt) throw new Error(this.outboundConnectTimeoutMessage);
      const callWorkspaceId = delegated?.workspaceId || this.workspaceService.workspaceId;
      const preferredFrom = await this.resolveFromNumberForCurrentContext(callWorkspaceId);
      this.log('start-call:attempt', {
        target,
        preferredFrom,
        workspaceId: callWorkspaceId,
        campaignId: callLeadContext?.campaignId,
        previousCallId: callLeadContext?.previousCallId,
        context: callLeadContext,
      });

      if (delegated && Date.now() > delegated.deadlineAt) throw new Error(this.outboundConnectTimeoutMessage);
      const prepared = await this.softphoneService.prepareSoftphoneCall({
        targetNumber: target,
        integrationId: this.softphoneService.selectedIntegrationId(),
        preferredFromNumber: preferredFrom,
        campaignId: callLeadContext?.campaignId,
        previousCallId: callLeadContext?.previousCallId,
        source: callLeadContext?.source || 'manual',
      }, delegated?.workspaceId);
      preparedCall = prepared;
      if (delegated && Date.now() > delegated.deadlineAt) throw new Error(this.outboundConnectTimeoutMessage);
      this.callState.update(current => ({
        ...current,
        callerIdMessage: prepared.fromNumber
          ? `Using ${this.formatPhoneForDisplay(prepared.fromNumber)} for this call.`
          : undefined,
      }));
      this.getDiagnostics().updateCapture(qualityId, { callId: prepared.callId, integrationId: prepared.integrationId });
      this.setActiveVoxrCallId(prepared.callId);
      this.softphoneService.publishSoftphoneState(this.state(), `Calling ${target}...`);
      delegated?.onProviderConnectStart(prepared.callId);
      const connectPromise = this.device.connect({
        params: {
          CallId: preparedCall.callId,
          To: target,
          ...(preparedCall.fromNumber ? { PreferredFrom: preparedCall.fromNumber } : (preferredFrom ? { PreferredFrom: preferredFrom } : {})),
          ...(preparedCall.integrationId ? { IntegrationId: preparedCall.integrationId } : {}),
          ...(callWorkspaceId ? { WorkspaceId: callWorkspaceId } : {}),
          ...(callLeadContext?.campaignId ? { CampaignId: callLeadContext.campaignId } : {}),
          ...(callLeadContext?.previousCallId ? { PreviousCallId: callLeadContext.previousCallId } : {}),
        }
      });
      const call = delegated
        ? await this.connectOutboundCallWithTimeout(connectPromise, Math.max(0, delegated.deadlineAt - Date.now()))
        : await connectPromise;
      this.log('start-call:created', {
        target,
        preferredFrom,
        callSid: call?.parameters?.['CallSid'],
      });
      this.attachCallListeners(call, false, qualityId, qualityDevice);
      this.beginOutgoingCall(target, call);
      this.softphoneService.publishActiveCall(await this.buildMirroredActiveCall(call));
      this.openPanel();
      return { ok: true };
    } catch (error: any) {
      this.getDiagnostics().updateCapture(qualityId, { callId: preparedCall?.callId });
      this.completeQualityAttempt(qualityId, 'preparation-failed');
      this.exceptions.record(error, { source: 'softphone', operation: 'start-call', callId: preparedCall?.callId });
      this.failOutgoingCallStart();
      if (preparedCall?.callId) {
        try {
          await this.softphoneService.releaseSoftphoneCallReservation(preparedCall.callId);
        } catch (releaseError) {
          this.log('start-call:release-reservation-error', { callId: preparedCall.callId, error: releaseError }, 'warn');
        }
      }
      this.finishOutgoingPreparation();
      this.log('start-call:error', { error }, 'error');
      const errorMessage = this.getFriendlyActionError(error, 'outbound-start');
      if (delegated) {
        if (delegatedPreviousState && this.state() === delegatedPreviousState.state) {
          this.softphoneService.publishSoftphoneState(delegatedPreviousState.state, delegatedPreviousState.detail);
        }
        return { ok: false, error: errorMessage };
      }
      this.softphoneService.publishSoftphoneState('error', errorMessage);
      this.app.message$.next({ severity: 'danger', summary: 'Soft phone', detail: errorMessage });
      return undefined;
    }
  }

  private connectOutboundCallWithTimeout(connectPromise: Promise<TwilioCallLike>, timeoutMs: number) {
    return new Promise<TwilioCallLike>((resolve, reject) => {
      let settled = false;
      const timeoutTimer = setTimeout(() => {
        settled = true;
        reject(new Error(this.outboundConnectTimeoutMessage));
      }, timeoutMs);

      connectPromise.then(call => {
        if (settled) {
          this.log('start-call:late-provider-call', { callSid: call?.parameters?.['CallSid'] }, 'warn');
          try { call.disconnect(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'late-call-disconnect-cleanup' }); }
          return;
        }
        settled = true;
        clearTimeout(timeoutTimer);
        resolve(call);
      }, error => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutTimer);
        reject(error);
      });
    });
  }

  private releasePreparedOutboundReservation(callId: string) {
    void this.softphoneService.releaseSoftphoneCallReservation(callId).catch(error => {
      this.log('start-call:release-reservation-error', { callId, error }, 'warn');
    });
  }

  private startPendingOutboundOwnerCommand(messageId: string) {
    this.clearPendingOutboundOwnerCommand();
    const deadlineAt = Date.now() + this.outboundConnectTimeoutMs;
    this.pendingOutboundOwnerCommand = {
      messageId,
      deadlineAt,
      timeout: setTimeout(() => {
        if (this.pendingOutboundOwnerCommand?.messageId !== messageId) return;
        if (this.pendingOutboundOwnerCommand.callId) {
          this.releasePreparedOutboundReservation(this.pendingOutboundOwnerCommand.callId);
        }
        this.clearPendingOutboundOwnerCommand();
        this.finishOutgoingPreparation();
        this.restoreMirroredSoftphoneState();
        this.app.message$.next({
          severity: 'danger',
          summary: 'Soft phone',
          detail: this.outboundConnectTimeoutMessage,
        });
      }, this.outboundConnectTimeoutMs),
    };
  }

  private clearPendingOutboundOwnerCommand() {
    if (this.pendingOutboundOwnerCommand?.timeout) clearTimeout(this.pendingOutboundOwnerCommand.timeout);
    this.pendingOutboundOwnerCommand = undefined;
  }

  private restoreMirroredSoftphoneState() {
    const mirrored = this.sessionState();
    this.softphoneService.publishSoftphoneState(mirrored.state, mirrored.detail);
  }

  goToActiveCallTab() {
    const activeCall = this.callState().remoteActive;
    if (!activeCall) {
      this.app.message$.next({ severity: 'info', summary: 'Soft phone', detail: 'The active call is no longer available.' });
      return;
    }
    this.softphoneService.sendOwnerCommand({
      type: 'focus-active-call',
      payload: { callId: activeCall.callId },
    });
  }

  goToIncomingTransferTab() {
    const notice = this.incomingTransferNotice();
    if (!notice?.targetTabId) return;
    this.softphoneService.focusIncomingTransferTab(notice.targetTabId,
      notice.incomingType === 'recovery' ? undefined : notice.transfer?._id?.toString());
  }

  private setPendingRemoteAcceptedCall() {
    const incomingTransfer = this.mirroredIncomingTransfer();
    const transferId = incomingTransfer?.incomingType === 'recovery' ? undefined : incomingTransfer?.transfer?._id?.toString();
    const callId = incomingTransfer?.call?._id?.toString();
    this.clearPendingRemoteAcceptedCall();
    this.pendingRemoteAcceptedCall = {
      transferId,
      callId,
      previousState: this.state(),
      previousDetail: this.stateDetail(),
    };
    this.pendingRemoteAcceptedCallTimeout = setTimeout(() => {
      const pending = this.pendingRemoteAcceptedCall;
      this.clearPendingRemoteAcceptedCall();
      if (!pending) return;
      this.softphoneService.publishSoftphoneState(pending.previousState, pending.previousDetail);
      this.app.message$.next({
        severity: 'warning',
        summary: 'Soft phone',
        detail: 'The active call could not be confirmed. Check the other VOXR tab or try again.',
      });
    }, 60_000);
  }

  private matchesPendingRemoteAcceptedCall(activeCall?: SoftphoneSessionActiveCallSummary) {
    const pending = this.pendingRemoteAcceptedCall;
    if (!pending || !activeCall) return false;
    return (!!pending.transferId && pending.transferId === activeCall.transferId)
      || (!!pending.callId && pending.callId === activeCall.callId);
  }

  private clearPendingRemoteAcceptedCall() {
    if (this.pendingRemoteAcceptedCallTimeout) clearTimeout(this.pendingRemoteAcceptedCallTimeout);
    this.pendingRemoteAcceptedCallTimeout = undefined;
    this.pendingRemoteAcceptedCall = undefined;
  }

  onDialNumberChange(value: string) {
    const dialValue = this.coercePhoneInput(value);
    this.updateDialDestination(dialValue);
    const callLeadContext = this.callLeadContext();
    if (callLeadContext && this.normalizeDialTarget(dialValue) !== callLeadContext.leadPhone) {
      this.callState.update(current => ({ ...current, leadContext: undefined }));
    }
  }

  onThreeWayNumberChange(value: string) {
    const dialValue = this.coercePhoneInput(value);
    this.threeWayNumber.set(dialValue);
    this.setStoredNumber(this.threeWayNumberStorageKey, dialValue);
  }

  removeLastDialDigit() {
    const nextDialNumber = this.dialerState().destination.slice(0, -1);
    this.updateDialDestination(nextDialNumber);
  }

  removeLastThreeWayDigit() {
    const nextDialNumber = this.threeWayNumber().slice(0, -1);
    this.threeWayNumber.set(nextDialNumber);
    this.setStoredNumber(this.threeWayNumberStorageKey, nextDialNumber);
  }

  formatPhoneForDisplay(value?: string) {
    const raw = value?.toString().trim() || '';
    if (!raw) return '';
    if (raw.startsWith('client:')) return raw;
    if (/[A-Za-z]/.test(raw) || /[*#]/.test(raw)) return raw;

    const hasPlus = raw.startsWith('+');
    const digits = raw.replace(/\D/g, '');
    if (!digits) return raw;

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    if (digits.length > 6 && digits.length < 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length > 3 && digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }

    return hasPlus ? `+${digits}` : digits;
  }

  acceptIncoming(automatic = false) {
    const call = automatic ? this.ownerIncomingCall : this.incomingCall() || this.ownerIncomingCall;
    this.log('ui:accept-incoming', { incomingCall: call, isOwner: this.softphoneService.isOwnerTab });
    if (!this.allowIncomingFallbackAction(call)) return;
    if (!this.softphoneService.isOwnerTab) {
      if (!this.softphoneService.canDelegateToOwner) {
        this.handleUnavailableIncomingTransfer();
        return;
      }
      this.setPendingRemoteAcceptedCall();
      this.softphoneService.sendOwnerCommand({
        type: 'accept-incoming-transfer',
        payload: {
          integrationId: this.mirroredIncomingTransfer()?.transfer?.integrationId
            || this.mirroredIncomingTransfer()?.call?.integrationId
            || this.softphoneService.selectedIntegrationId()
        },
      });
      this.softphoneService.publishSoftphoneState('loading', 'Accepting transfer in owner tab...');
      return;
    }
    if (!call) {
      this.app.message$.next({ severity: 'info', summary: 'Soft phone', detail: 'Transfer is still connecting. Please try again in a moment.' });
      return;
    }
    if (this.incomingAutoAnswer?.call === call) {
      if (this.incomingAutoAnswer.handled) return;
      this.incomingAutoAnswer.handled = true;
      if (!automatic) this.incomingAutoAnswer.reason = 'manual';
      this.incomingAutoAnswer.cancelBeep?.();
    }
    void this.reacquireWakeLock();
    call.accept();
  }

  rejectIncoming() {
    const call = this.incomingCall() || this.ownerIncomingCall;
    this.log('ui:reject-incoming', { incomingCall: call, isOwner: this.softphoneService.isOwnerTab });
    if (!this.allowIncomingFallbackAction(call)) return;
    if (!this.softphoneService.isOwnerTab) {
      if (!this.softphoneService.canDelegateToOwner) {
        this.handleUnavailableIncomingTransfer();
        return;
      }
      this.softphoneService.sendOwnerCommand({
        type: 'reject-incoming-transfer',
        payload: {
          integrationId: this.mirroredIncomingTransfer()?.transfer?.integrationId
            || this.mirroredIncomingTransfer()?.call?.integrationId
            || this.softphoneService.selectedIntegrationId()
        },
      });
      this.softphoneService.publishSoftphoneState('loading', 'Rejecting transfer in owner tab...');
      return;
    }
    if (!call) {
      this.app.message$.next({ severity: 'info', summary: 'Soft phone', detail: 'Transfer is still connecting. Please try again in a moment.' });
      return;
    }
    const genericIncomingCall = !this.transferContext();
    call.reject();
    this.incomingCall.set(undefined);
    this.callState.update(current => ({ ...current, incoming: undefined }));
    this.clearIncomingTransferState(undefined, call);
    this.clearActiveTransfer('incoming-rejected-ui');
    this.clearTransferContext('incoming-rejected-ui');
    this.softphoneService.publishSoftphoneState(this.state(), genericIncomingCall ? 'Call declined' : 'Transfer rejected.');
  }

  endCall() {
    if (this.hasConferenceParticipants() && this.conferenceMutationPending()) return;
    this.log('ui:end-call', { activeCall: this.activeCall(), hasConference: this.participantRows().length > 0 });
    if (!this.softphoneService.isOwnerTab) {
      const callId = this.getActiveCallIdForParticipantOps();
      if (!callId) {
        this.app.message$.next({ severity: 'info', summary: 'Soft phone', detail: 'Call context is still loading. Please try again in a moment.' });
        return;
      }
      this.softphoneService.sendOwnerCommand({
        type: 'hang-up',
        payload: { callId },
      });
      this.softphoneService.publishSoftphoneState('loading', 'Ending call in owner tab...');
      return;
    }
    this.setLocalEnd(true);
    this.softphoneService.queueTerminationObservation('user-hangup');
    try { this.activeCall()?.disconnect(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'call-disconnect' }); }
    if (!this.participantRows().length) {
      try { this.device?.disconnectAll?.(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'device-disconnect-all' }); }
    }
    this.finishOutgoingPreparation();
    const endingCall = this.activeCall();
    if (endingCall) this.finishCall(endingCall);
    this.resetCallUiState();
    this.clearActiveTransfer('end-call');
    this.publishReadyState();
    this.softphoneService.publishActiveCall(undefined);
  }

  async leaveConference() {
    if (this.conferenceMutationPending() || !this.hasConferenceParticipants()) return;
    const conferenceCallId = await this.ensureActiveCallId();
    if (!conferenceCallId || !this.hasConferenceParticipants()) return;
    const confirmed = await firstValueFrom(this.dialog.open(this.leaveConferenceConfirmation, {
      width: '420px',
      maxWidth: '95vw',
      autoFocus: false,
      restoreFocus: false,
    }).afterClosed());
    if (
      !confirmed
      || this.conferenceMutationPending()
      || !this.hasConferenceParticipants()
      || this.getActiveCallIdForParticipantOps() !== conferenceCallId
    ) return;
    this.endCall();
  }

  async startThreeWayCall() {
    const activeCall = this.activeCall();
    const readiness = {
      actor: 'agent',
      reason: this.threeWayReadinessReason(),
      ready: this.canStartThreeWay(),
      transferMode: this.activeTransferMode(),
      callProgress: this.callProgress(),
      reconnecting: this.threeWayConnectionRecovering(),
      hasTransferContext: !!this.getActiveTransferId(),
      callIdAvailable: !!this.getActiveCallIdForParticipantOps(),
      callSidAvailable: !!this.getCallSid(activeCall),
      mutationPending: this.conferenceMutationPending(),
    };
    this.log('three-way:submit', readiness);
    if (!readiness.ready || !activeCall) {
      this.log('three-way:blocked', readiness, 'warn');
      return;
    }

    const targetNumber = this.normalizeDialTarget(this.threeWayNumber());
    const callSid = this.getCallSid(activeCall);
    const activityId = this.getActiveTransferId();
    if (!targetNumber) {
      this.app.message$.next({ severity: 'warning', summary: 'Soft phone', detail: 'Three-way number is required.' });
      return;
    }
    if (!callSid) {
      this.app.message$.next({ severity: 'warning', summary: 'Soft phone', detail: 'Active call SID is not available for three-way call.' });
      return;
    }

    this.placingThreeWay.set(true);
    try {
      const callId = await this.ensureActiveCallId();
      if (!callId) {
        this.log('three-way:blocked', { ...readiness, reason: 'call-context-unavailable' }, 'warn');
        this.app.message$.next({ severity: 'warning', summary: 'Soft phone', detail: 'Call context is still loading. Please try again in a moment.' });
        return;
      }

      this.softphoneService.publishSoftphoneState(this.state(), 'Starting three-way call...');
      this.setStoredNumber(this.threeWayNumberStorageKey, targetNumber);
      const response = await this.softphoneService.threeWayCall({ callSid, callId, targetNumber, activityId });
      this.callState.update(current => ({ ...current, threeWayAccepted: true }));
      this.log('three-way:accepted', {
        actor: 'agent',
        callSid,
        activityId,
        threeWayCallSid: response?.threeWayCallSid,
        conferenceName: response?.conferenceName,
        conferenceSid: response?.conferenceSid,
        callId: response?.callId,
      });
      if (!this.callState().childLegStartedAt) {
        this.callState.update(current => ({ ...current, childLegStartedAt: Date.now() }));
        this.startUiTimer();
      }
      if (response?.callId) this.setActiveVoxrCallId(response.callId);
      void this.refreshParticipants().then(refreshed => {
        if (refreshed) this.log('three-way:snapshot-applied', { actor: 'agent', callId, callSid, activityId });
      });
    } catch (error: any) {
      this.log('three-way:failed', { actor: 'agent', error: this.summarizeError(error) }, 'error', error);
      this.app.message$.next({ severity: 'danger', summary: 'Soft phone', detail: this.getFriendlyActionError(error, 'three-way-start') });
    } finally {
      this.placingThreeWay.set(false);
      this.publishReadyState();
    }
  }

  private async initializeSoftphone(force = false) {
    if (!this.enabled()) return;
    if (this.device && !force) return;

    let initStage = 'start';
    let didFetchCredential = false;
    let deviceBound = false;

    this.beginRuntimeInitialization();
    this.softphoneService.publishSoftphoneState('loading', 'Connecting soft phone...');
    this.log('init:start', { force });

    try {
      initStage = 'acquire-device';
      this.device = await this.softphoneService.acquireSoftphoneDevice('softphone', {
        forceRecreate: force,
        ensureMicrophone: true,
      });
      if (!this.device) {
        this.log('init:non-owner', { force });
        return;
      }

      initStage = 'read-diagnostics';
      const diagnostics = this.softphoneService.twilioDiagnostics;
      this.updateDiagnosticTokenFetchAt(diagnostics.lastTokenFetchAt);
      didFetchCredential = !!diagnostics.lastTokenFetchAt;

      initStage = 'subscribe-device-events';
      deviceBound = true;
      // device.register() is awaited inside softphoneService before returning, so the
      // 'registered' event fires before our listener is attached. Transition to ready immediately
      // if the device is already in registered state.
      initStage = 'device-ready-check';
      if ((this.device as any)?.state === 'registered') {
        initStage = 'device-registered-handler';
        await this.onDeviceRegistered(this.device);
      }
    } catch (error: any) {
      this.log('init:error', {
        stage: initStage,
        force,
        hasDevice: !!this.device,
        didFetchCredential,
        deviceBound,
        error: this.summarizeError(error),
      }, 'error', error);
      this.softphoneService.publishSoftphoneState('error', this.getFriendlyActionError(error, 'connection'));
      await this.softphoneService.publishOwnerRuntimeSnapshot('connection-change', { activeCall: null, enabled: this.enabled() });
    } finally {
      this.finishRuntimeInitialization();
    }
  }

  private async reconnectConnections(integrationIds: string[]) {
    if (!integrationIds.length) {
      await this.reconnect();
      return;
    }

    const reconnectResults = await Promise.allSettled(integrationIds.map(integrationId =>
      this.softphoneService.recoverSoftphoneDevice('softphone', {
        integrationId,
        forceRecreate: true,
      })
    ));
    const failures = reconnectResults
      .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
      .map(result => result.reason);

    if (failures.length > 0) {
      const firstFailure = failures[0];
      const firstMessage = firstFailure instanceof Error
        ? firstFailure.message
        : typeof firstFailure?.message === 'string'
          ? firstFailure.message
          : undefined;
      throw new Error(
        failures.length === 1
          ? (firstMessage || 'Twilio connection could not reconnect.')
          : `${failures.length} Twilio connections could not reconnect.${firstMessage ? ` First error: ${firstMessage}` : ''}`
      );
    }
  }

  private summarizeConnections(connections: SoftphoneConnectionVm[]): SoftphoneConnectionSummary {
    const connectedCount = connections.filter(connection => connection.status === 'connected').length;
    const inProgressCount = connections.filter(connection =>
      connection.status === 'connecting' || connection.status === 'retrying'
    ).length;
    const failedCount = connections.filter(connection =>
      connection.status === 'error' || connection.status === 'disconnected'
    ).length;
    const totalCount = connections.length;

    return {
      totalCount,
      connectedCount,
      inProgressCount,
      failedCount,
      partialFailure: totalCount > 1 && failedCount > 0 && connectedCount > 0,
      allFailed: totalCount > 0 && connectedCount === 0 && inProgressCount === 0 && failedCount === totalCount,
    };
  }

  private getConnectionDisplayStatus(status: SoftphoneMirroredConnectionStatus): SoftphoneMirroredConnectionStatus {
    return status;
  }

  private getConnectionStatusLabel(status: SoftphoneMirroredConnectionStatus) {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting';
      case 'retrying':
        return 'Reconnecting';
      case 'error':
        return 'Error';
      default:
        return 'Inactive';
    }
  }

  private buildIntegrationOptionTooltip(title: string, integrationId: string, accountSid?: string) {
    const lines = [title, `Integration ID: ${integrationId}`];
    if (accountSid) lines.push(`Account SID: ${accountSid}`);
    return lines.join('\n');
  }

  private getConnectionIssueSummary(status: SoftphoneMirroredConnectionStatus, error?: string) {
    const normalizedError = error?.replace(/\s+/g, ' ').trim();
    if (normalizedError?.includes('SOFTPHONE_PROVIDER_AUTH_FAILED')
      || normalizedError?.includes('Twilio credentials were rejected.')) {
      return 'Twilio credentials were rejected. Contact an admin.';
    }
    if (status === 'error') {
      return 'Connection could not connect. Try reconnecting or contact an admin.';
    }
    if (status === 'disconnected') {
      return 'Connection is unavailable. Try reconnecting or contact an admin.';
    }
    return undefined;
  }

  private getFriendlyPrimaryErrorMessage(error?: string) {
    if (error?.includes('SOFTPHONE_PROVIDER_AUTH_FAILED') || error?.includes('Twilio credentials were rejected.')) {
      return 'Twilio credentials were rejected. Contact an admin.';
    }
    return 'Soft phone could not connect. Try reconnecting or contact an admin.';
  }

  private onDeviceRegistered = async (device = this.device) => {
    if (!device || device !== this.device || this.registeredDevice === device) return;
    this.registeredDevice = device;
    this.log('device:registered', { sid: (this.device as any)?.sid });
    this.softphoneService.publishSoftphoneState('ready', this.readyDetail);
    void this.acquireWakeLock();
    await this.softphoneService.publishOwnerRuntimeSnapshot('connection-change', { enabled: this.enabled() });
  };

  private onDeviceUnregistered = async (device = this.device) => {
    if (!device || device !== this.device) return;
    this.registeredDevice = undefined;
    if (this.runtimeState().destroying || !this.enabled()) {
      this.log('device:unregistered:intentional', { destroying: this.runtimeState().destroying, enabled: this.enabled() });
      // Ensure any held Wake Lock is released when intentionally destroyed
      this.releaseWakeLock();
      await this.softphoneService.publishOwnerRuntimeSnapshot('shutdown', { activeCall: null, enabled: this.enabled() });
      return;
    }
    this.log('device:unregistered:runtime-managed', {
      reason: 'unregistered',
      activeCallSid: this.getCallSid(this.activeCall()),
      callProgress: this.callProgress(),
      visibilityState: document.visibilityState,
    });
    if (this.hasUnloadProtectedCallState() || this.device?.isBusy) void this.reacquireWakeLock();
    else this.releaseWakeLock();
  };

  private onDeviceError = async (error: any, device = this.device) => {
    if (!device || device !== this.device) return;
    this.log('device:error', {
      error: this.summarizeError(error),
      activeCallSid: this.getCallSid(this.activeCall()),
      callProgress: this.callProgress(),
      visibilityState: document.visibilityState,
    }, 'error', error);
    if (this.hasUnloadProtectedCallState() || this.device?.isBusy) void this.reacquireWakeLock();
    else this.releaseWakeLock();
  };

  private attachCallListeners(call: TwilioCallLike, incoming = false, existingQualityId?: string, device = this.device) {
    if (this.qualityCalls.has(call)) return;
    const id = existingQualityId || this.getDiagnostics().startCapture({ direction: incoming ? 'incoming' : 'outgoing' });
    const refresh = () => { try { this.getDiagnostics().updateCapture(id, this.qualityCallContext(call, device)); } catch { /* Optional context unavailable. */ } };
    refresh();
    const sample = (value: unknown) => this.zone.runOutsideAngular(() => {
      this.getDiagnostics().recordSample(id, value);
      if (this.isLiveQualityCall(call)) this.updateCallQuality(value);
    });
    const warning = (name: unknown) => this.zone.runOutsideAngular(() => {
      this.getDiagnostics().recordWarning(id, name, false);
      if (typeof name !== 'string') return;
      if (name === 'ice-connectivity-lost') this.markConnectionIssue(call, true);
      if (!this.isLiveQualityCall(call)) return;
      this.activeQualityWarnings.add(name);
      this.updateCallQuality();
    });
    const cleared = (name: unknown) => this.zone.runOutsideAngular(() => {
      this.getDiagnostics().recordWarning(id, name, true);
      if (typeof name !== 'string') return;
      if (name === 'ice-connectivity-lost') this.markConnectionIssue(call, false);
      if (!this.isLiveQualityCall(call)) return;
      this.activeQualityWarnings.delete(name);
      this.updateCallQuality();
    });
    this.zone.runOutsideAngular(() => { call.on('sample', sample); call.on('warning', warning); call.on('warning-cleared', cleared); });
    let deviceObservation: { references: number; listener: () => void } | undefined;
    if (device?.audio?.on) {
      this.qualityDevices ||= new Map();
      deviceObservation = this.qualityDevices.get(device);
      if (!deviceObservation) {
        deviceObservation = { references: 0, listener: () => {
          for (const [observedCall, entry] of this.qualityCalls) {
            if (entry.device !== device) continue;
            try { this.getDiagnostics().updateCapture(entry.id, this.qualityCallContext(observedCall, device)); } catch { /* Optional context unavailable. */ }
          }
        } };
        this.qualityDevices.set(device, deviceObservation);
        device.audio.on('deviceChange', deviceObservation.listener);
      }
      deviceObservation.references++;
    }
    let cleaned = false;
    this.qualityCalls.set(call, { id, device, cleanup: () => {
      if (cleaned) return;
      cleaned = true;
      for (const [event, listener] of [['sample', sample], ['warning', warning], ['warning-cleared', cleared]] as const) {
        try { call.off(event, listener); } catch { /* Continue detaching other diagnostic listeners. */ }
      }
      if (device && deviceObservation && --deviceObservation.references === 0) {
        try { device.audio?.off?.('deviceChange', deviceObservation.listener); } catch { /* Device teardown is best effort. */ }
        this.qualityDevices.delete(device);
      }
    } });
    call.on('ringing', () => {
      this.getDiagnostics().updateCapture(id, { ringingAt: new Date().toISOString() });
      if (this.activeCall() === call || this.incomingCall() === call) {
        this.setCallProgress('ringing');
      }
    });

    call.on('accept', async () => {
      try { this.getDiagnostics().updateCapture(id, { ...this.qualityCallContext(call), acceptedAt: new Date().toISOString() }); } catch { /* Optional context unavailable. */ }
      this.log('call:accepted', { incoming: !!incoming, call });
      if (this.incomingAutoAnswer?.call === call && this.incomingAutoAnswer.reason && this.incomingAutoAnswer.reason !== 'manual') {
        this.log('auto-answer:accepted', { mode: this.incomingAutoAnswer.mode });
      }
      if (!incoming) this.finishOutgoingPreparation();
      if (incoming) this.clearIncomingTransferState(undefined, call);
      this.connectCall(call, !!incoming);
      if (!this.callState().mainStartedAt) {
        this.callState.update(current => ({ ...current, mainStartedAt: Date.now() }));
      }
      this.startUiTimer();
      this.softphoneService.publishSoftphoneState(this.state(), this.transferContext()?.incomingType === 'recovery'
        ? 'Recovery call' : incoming && this.transferContext() ? 'Transfer connected' : 'Call connected');
      let activeCallPublished = false;
      const mirroredActiveCall = await this.buildMirroredActiveCall(call);
      if (incoming) {
        try {
          await this.softphoneService.publishActiveCall(mirroredActiveCall);
          activeCallPublished = true;
        } catch (error: any) {
          this.log('call:active-snapshot:error', {
            incoming: true,
            callSid: this.getCallSid(call),
            callId: this.getCallParam(call, 'CallId') || this.callState().voxrCallId,
            error: this.summarizeError(error),
          }, 'warn', error);
        }
      } else {
        void this.softphoneService.publishActiveCall(mirroredActiveCall);
      }
      void this.syncActiveTransferForCall(call, 'accept');
      this.resolveAndSetCallId(call);
      if (incoming && activeCallPublished) void this.refreshParticipants();
    });

    call.on('disconnect', () => {
      this.log('call:disconnected', {
        callSid: this.getCallSid(call),
        direction: incoming ? 'incoming' : 'outgoing',
      });
      this.explainRemoteCallEnd(call);
      this.finalizeCallTransition(call, 'call-disconnect', this.readyDetail, incoming);
    });

    call.on('reconnecting', (error: any) => {
      if (this.activeCall() !== call) {
        this.log('call:reconnecting:ignored', {
          callSid: this.getCallSid(call),
          activeCallSid: this.getCallSid(this.activeCall()),
          direction: incoming ? 'incoming' : 'outgoing',
        }, 'warn');
        return;
      }
      const capture = this.qualityCalls.get(call);
      if (capture) this.getDiagnostics().recordWarning(capture.id, 'reconnecting', false);
      this.markConnectionIssue(call, true);
      this.threeWayConnectionRecovering.set(true);
      if (this.isLiveQualityCall(call)) this.updateCallQuality();
      this.log('call:reconnecting', {
        error: this.summarizeError(error),
        callSid: this.getCallSid(call),
        direction: incoming ? 'incoming' : 'outgoing',
      }, 'warn', error);
    });

    call.on('reconnected', async () => {
      if (this.activeCall() !== call) {
        this.log('call:reconnected:ignored', {
          callSid: this.getCallSid(call),
          activeCallSid: this.getCallSid(this.activeCall()),
          direction: incoming ? 'incoming' : 'outgoing',
        }, 'warn');
        return;
      }
      const capture = this.qualityCalls.get(call);
      if (capture) this.getDiagnostics().recordWarning(capture.id, 'reconnecting', true);
      this.markConnectionIssue(call, false);
      this.threeWayConnectionRecovering.set(false);
      if (this.isLiveQualityCall(call)) this.updateCallQuality();
      this.log('call:reconnected', {
        callSid: this.getCallSid(call),
        direction: incoming ? 'incoming' : 'outgoing',
      });
      try {
        const mirroredActiveCall = await this.buildMirroredActiveCall(call);
        if (this.activeCall() !== call) return;
        await this.softphoneService.publishActiveCall(mirroredActiveCall);
        if (this.activeCall() !== call || !this.hasConferenceContext()) return;
        await this.refreshParticipants();
        if (this.activeCall() !== call) return;
      } catch (error: any) {
        if (this.activeCall() !== call) return;
        this.log('call:reconnected:reconcile:error', {
          callSid: this.getCallSid(call),
          direction: incoming ? 'incoming' : 'outgoing',
          error: this.summarizeError(error),
        }, 'warn', error);
      }
    });

    call.on('cancel', () => {
      this.log('call:canceled', { call });
      this.finalizeCallTransition(call, 'call-cancel', 'Incoming call canceled', incoming);
    });

    call.on('reject', () => {
      this.log('call:rejected-by-agent', { call });
      this.finalizeCallTransition(call, 'call-reject', incoming && !this.transferContext() ? 'Call declined' : 'Transfer rejected', incoming);
    });

    call.on('error', (error: any) => {
      const accepted = this.callWasAccepted(call);
      // With answerOnBridge, an outbound dial that ends before answer surfaces as an SDK error; only an
      // underlying SIP final response confirms busy, declined, or not answered.
      const outcome = !incoming && accepted === false ? this.confirmedDialOutcome(error) : undefined;
      const networkDrop = accepted === true && this.endedByNetwork(call);
      this.finishQualityCall(call, 'error');
      if (this.shouldIgnoreStaleTerminalEvent(call, 'call-error')) return;
      this.log('call:error', {
        error,
        callSid: this.getCallSid(call),
        direction: incoming ? 'incoming' : 'outgoing',
      }, 'error');
      this.log('call:ended:classified', {
        kind: outcome?.kind || (networkDrop ? 'network-drop' : 'other'),
        code: error?.code, sipCode: error?.originalError?.code,
      });
      if (!outcome) this.softphoneService.queueTerminationObservation('sdk-error');
      this.finishCall(call);
      this.clearMirroredCallState(call, incoming);
      this.resetCallUiState();
      this.clearActiveTransfer('call-error');
      if (outcome) {
        this.publishReadyState();
        this.app.message$.next({ severity: 'info', summary: 'Soft phone', detail: outcome.message });
      } else {
        const message = networkDrop ? this.networkDropMessage : this.getFriendlyActionError(error, 'call');
        this.softphoneService.publishSoftphoneState('error', message);
        this.app.message$.next({ severity: networkDrop ? 'warning' : 'danger', summary: 'Soft phone', detail: message });
      }
      void this.softphoneService.publishOwnerRuntimeSnapshot('connection-change', { activeCall: null, enabled: this.enabled() });
    });
  }

  private finalizeCallTransition(
    call: TwilioCallLike,
    reason: string,
    readyDetail = this.readyDetail,
    incoming = false,
  ) {
    this.finishQualityCall(call, reason.replace('call-', ''));
    if (this.shouldIgnoreStaleTerminalEvent(call, reason)) return;
    this.finishCall(call);
    this.clearMirroredCallState(call, incoming);
    this.resetCallUiState();
    this.clearActiveTransfer(reason);
    this.publishReadyState(readyDetail);
  }

  private shouldIgnoreStaleTerminalEvent(call: TwilioCallLike, reason: string) {
    const controllingCall = this.activeCall() || this.incomingCall();
    if (!controllingCall || controllingCall === call) return false;

    this.log('call:terminal-ignored', {
      reason,
      terminalCallSid: this.getCallSid(call),
      controllingCallSid: this.getCallSid(controllingCall),
    }, 'warn');
    return true;
  }

  toggleMute() {
    if (this.conferenceMutationPending()) return;
    const call = this.activeCall() as any;
    if (!call || typeof call.mute !== 'function') return;

    const nextMuted = !this.callState().muted;
    try {
      call.mute(nextMuted);
      const capture = this.qualityCalls.get(call);
      if (capture) this.getDiagnostics().updateCapture(capture.id, { mute: nextMuted });
      this.callState.update(current => ({ ...current, muted: nextMuted }));
    } catch (error: any) {
      this.exceptions.record(error, { source: 'softphone', operation: 'call-mute' });
      this.app.message$.next({ severity: 'danger', summary: 'Soft phone', detail: this.getFriendlyActionError(error, 'mute') });
    }
  }

  async toggleHold() {
    if (this.conferenceMutationPending()) return;
    const callId = await this.ensureActiveCallId();
    const agentCallSid = this.getCallSid(this.activeCall());
    if (!callId || !agentCallSid) return;

    const action = this.callState().onHold ? 'unhold' : 'hold';
    try {
      this.pendingAgentHoldAction.set(true);
      const response = await this.conferenceSession.act(agentCallSid, action);
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      if (!response?.stale) this.callState.update(current => ({ ...current, onHold: !current.onHold }));
    } catch (error: any) {
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      this.exceptions.record(error, { source: 'softphone', operation: 'participant-hold', callId });
      this.app.message$.next({ severity: 'danger', summary: 'Soft phone', detail: this.getFriendlyActionError(error, 'participant-hold') });
    } finally {
      this.pendingAgentHoldAction.set(false);
    }
  }

  private startUiTimer() {
    if (this.uiTimer) return;
    this.uiTimer = setInterval(() => {
      const mainStartedAt = this.callState().mainStartedAt;
      const childStartedAt = this.callState().childLegStartedAt;

      if (mainStartedAt) {
        this.callState.update(current => ({ ...current, mainDurationSeconds: Math.max(0, Math.floor((Date.now() - mainStartedAt) / 1000)) }));
      }
      if (childStartedAt) {
        this.callState.update(current => ({ ...current, childLegDurationSeconds: Math.max(0, Math.floor((Date.now() - childStartedAt) / 1000)) }));
      }

      if (!mainStartedAt && !childStartedAt) {
        this.stopUiTimer();
      }
    }, 1000);
  }

  private stopUiTimer() {
    if (this.uiTimer) clearInterval(this.uiTimer);
    this.uiTimer = undefined;
  }

  private resetCallUiState() {
    this.finishOutgoingPreparation();
    this.callState.update(current => ({
      ...current,
      progress: 'idle',
      voxrCallId: undefined,
      callerIdMessage: undefined,
      muted: false,
      onHold: false,
      mainStartedAt: undefined,
      childLegStartedAt: undefined,
      mainDurationSeconds: 0,
      childLegDurationSeconds: 0,
      threeWayAccepted: false,
    }));
    this.panelState.update(current => ({ ...current, dtmfExpanded: false }));
    this.threeWayConnectionRecovering.set(false);
    this.conferenceSession.reset();
    this.pendingAgentHoldAction.set(false);
    this.stopUiTimer();
    if (!this.activeCall() && !this.incomingCall() && !this.mirroredIncomingTransfer()) {
      this.clearDialer();
      this.clearOriginPreview();
      this.clearTransferContext('reset-call-ui');
    }
  }

  private resolveAndSetCallId(call: TwilioCallLike) {
    if (this.callState().voxrCallId) return;
    const transferContext = this.transferContext();
    const callId = this.getCallParam(call, 'CallId')
      || transferContext?.call?._id?.toString()?.trim()
      || transferContext?.transfer?.callId?.toString()?.trim();
    if (!callId) return;
    this.setActiveVoxrCallId(callId);
    this.log('call:resolved-id', { callId });
  }

  async refreshParticipants(showError = false) {
    try {
      return await this.conferenceSession.refresh();
    } catch (error: any) {
      this.log('participants:refresh:error', { error }, 'warn');
      if (showError) {
        this.app.message$.next({ severity: 'warning', summary: 'Soft phone', detail: this.getFriendlyActionError(error, 'participant-refresh') });
      }
      return false;
    }
  }

  private reconcileParticipantSnapshot(payload?: SoftphoneParticipantSnapshotDto) {
    void this.conferenceSession.reconcile(payload).catch(error => {
      this.log('participants:refresh:error', { error }, 'warn');
    });
  }

  async onParticipantAction(participantCallSid: string, action: 'mute' | 'unmute' | 'hold' | 'unhold') {
    if (this.conferenceMutationPending()) return;
    const callId = await this.ensureActiveCallId();
    if (!callId || !participantCallSid) return;

    try {
      await this.conferenceSession.act(participantCallSid, action);
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
    } catch (error: any) {
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      this.log('participants:action:error', { error, participantCallSid, action }, 'warn');
      await this.handleConferenceMutationFailure(error, 'participant-action');
    }
  }

  async onMainParticipantAction(action: 'mute' | 'unmute' | 'hold' | 'unhold') {
    const callSid = this.mainParticipantLeg()?.callSid;
    if (!callSid) return;
    await this.onParticipantAction(callSid, action);
  }

  async onThirdParticipantAction(action: 'mute' | 'unmute' | 'hold' | 'unhold') {
    if (this.thirdParticipantEnded()) return;
    const callSid = this.thirdParticipantLeg()?.callSid;
    if (!callSid) return;
    await this.onParticipantAction(callSid, action);
  }

  async endParticipant(participantCallSid: string) {
    if (this.conferenceMutationPending()) return;
    const callId = await this.ensureActiveCallId();
    if (!callId || !participantCallSid) return;

    try {
      await this.conferenceSession.remove(participantCallSid);
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
    } catch (error: any) {
      this.setLocalEnd(false);
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      this.log('participants:end:error', { error, participantCallSid }, 'warn');
      await this.handleConferenceMutationFailure(error, 'participant-end');
    }
  }

  async endMainParticipant() {
    const callSid = this.mainParticipantLeg()?.callSid;
    if (!callSid) return;
    // Without a connected third leg, removing the main participant ends the agent's call as well.
    if (!this.isThirdLegConnected()) this.setLocalEnd(true);
    await this.endParticipant(callSid);
  }

  async endThirdParticipant() {
    if (this.thirdParticipantEnded()) return;
    const callSid = this.thirdParticipantLeg()?.callSid;
    if (!callSid) return;
    await this.endParticipant(callSid);
  }

  dismissEndedThirdParticipant() {
    if (this.conferenceMutationPending()) return;
    const callSid = this.thirdParticipantLeg()?.callSid;
    if (!callSid || !this.conferenceSession.dismissEndedThirdParticipant(callSid)) return;
    this.callState.update(current => ({ ...current, threeWayAccepted: false, childLegStartedAt: undefined }));
  }

  async endConference() {
    if (this.conferenceMutationPending()) return;
    const callId = await this.ensureActiveCallId();
    if (!callId) return;
    const confirmed = await firstValueFrom(this.dialog.open(this.endConferenceConfirmation, {
      width: '420px',
      maxWidth: '95vw',
      autoFocus: false,
      restoreFocus: false,
    }).afterClosed());
    if (!confirmed || this.conferenceMutationPending()) return;

    try {
      this.setLocalEnd(true);
      await this.conferenceSession.endAll();
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      this.callState.update(current => ({ ...current, childLegStartedAt: undefined }));
    } catch (error: any) {
      this.setLocalEnd(false);
      if (this.getActiveCallIdForParticipantOps() !== callId) return;
      this.log('conference:end:error', { error }, 'warn');
      await this.handleConferenceMutationFailure(error, 'conference-end');
    }
  }

  private async handleConferenceMutationFailure(error: any, action: SoftphoneActionKind) {
    const refreshed = await this.refreshParticipants(false);
    const baseMessage = this.getFriendlyActionError(error, action);
    this.app.message$.next({
      severity: 'danger',
      summary: 'Soft phone',
      detail: refreshed
        ? baseMessage
        : `${baseMessage} Conference status could not be refreshed. Close and reopen the softphone to try again.`,
    });
  }

  private async ensureActiveCallId(call: TwilioCallLike | undefined = this.activeCall() || this.incomingCall()) {
    const existingCallId = this.getActiveCallIdForParticipantOps();
    if (existingCallId) return existingCallId;

    const transferContext = this.transferContext();
    const callId = this.getCallParam(call, 'CallId')
      || transferContext?.call?._id?.toString()?.trim()
      || transferContext?.transfer?.callId?.toString()?.trim();
    if (!callId) return undefined;
    this.setActiveVoxrCallId(callId);
    this.log('call:resolved-id', { callId });
    void this.refreshParticipants();
    return callId;
  }

  private getActiveCallIdForParticipantOps() {
    const transfer = this.activeTransfer();
    const transferCallId = transfer?.callId?.toString()?.trim();
    if (transferCallId) return transferCallId;
    return this.callState().voxrCallId || undefined;
  }

  private applyConferenceTiming(rows: SoftphoneParticipantRow[]) {
    const activeThirdStartedAt = rows
      .filter(row => row.legType === 'third' && !!row.startedAt && !row.endedAt)
      .sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0))[0]?.startedAt;

    if (activeThirdStartedAt) {
      this.callState.update(current => ({ ...current, childLegStartedAt: activeThirdStartedAt }));
      this.startUiTimer();
    } else if (!rows.some(row => row.legType === 'third')) {
      this.callState.update(current => ({ ...current, childLegStartedAt: undefined }));
    }
  }

  private getFriendlyActionError(error: any, kind: SoftphoneActionKind) {
    const fallbackByKind: Record<SoftphoneActionKind, string> = {
      'outbound-start': 'Failed to start the outbound call. Please try again.',
      'call': 'The call ended unexpectedly. Please try again.',
      'caller-id': 'Unable to save caller ID preference. Please try again.',
      'command': 'The soft phone action could not be completed. Please try again.',
      'connection': 'Soft phone could not connect. Try reconnecting or contact an admin.',
      'three-way-start': 'Failed to start the three-way call. Please try again.',
      'mute': 'Unable to change mute state.',
      'participant-refresh': 'Unable to refresh participants.',
      'participant-action': 'Unable to update participant state.',
      'participant-end': 'Unable to end participant.',
      'participant-hold': 'Unable to change hold state.',
      'conference-end': 'Unable to end conference.',
    };

    const fallback = fallbackByKind[kind];
    const source = error?.cause || error;
    if (kind === 'command' && safeOwnerCommandErrors.has(source?.message)) return source.message;
    if (source?.message === this.outboundConnectTimeoutMessage) return this.outboundConnectTimeoutMessage;
    if (source?.name === 'NotAllowedError') return 'Microphone access is required to use the soft phone.';

    const httpStatus = Number(source?.status);
    const errorCode = typeof source?.error?.code === 'string' ? source.error.code : source?.code;
    if (errorCode === 'SOFTPHONE_PROVIDER_AUTH_FAILED') return 'Twilio credentials were rejected. Contact an admin.';
    if (httpStatus === 0) return 'The call control service could not be reached. Check your connection and try again.';
    if (httpStatus === 403) return 'You do not have permission to perform this action.';
    if (httpStatus === 409) return 'This action conflicts with the current call state. Refresh and try again.';
    if (httpStatus === 422) return 'This action is no longer valid for the current call state.';
    if (httpStatus >= 500) return 'The call control service is temporarily unavailable. Please try again.';
    return fallback;
  }

  private publishReadyState(detail = this.readyDetail) {
    if (this.enabled() && this.state() !== 'error') {
      this.softphoneService.publishSoftphoneState(this.state(), detail);
    }
  }

  private setStoredNumber(key: string, value: string) {
    if (!value?.trim()) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, value);
  }

  private getStoredBoolean(key: string, defaultValue: boolean) {
    const value = localStorage.getItem(key);
    if (value === null) return defaultValue;
    return value === 'true';
  }

  private setStoredBoolean(key: string, value: boolean) {
    localStorage.setItem(key, value ? 'true' : 'false');
  }

  private async buildMirroredActiveCall(call: TwilioCallLike | undefined): Promise<SoftphoneSessionActiveCallSummary | undefined> {
    if (!call || this.callProgress() === 'idle' || this.callProgress() === 'ended') return undefined;
    const transferContext = this.transferContext();
    const callId = await this.ensureActiveCallId(call);
    return {
      callId,
      callSid: this.getCallSid(call),
      transferId: transferContext?.incomingType === 'recovery' ? undefined
        : this.getCallParam(call, 'TransferId') || transferContext?.transfer?._id?.toString(),
      integrationId: this.getCallParam(call, 'IntegrationId')
        || transferContext?.transfer?.integrationId?.toString()
        || transferContext?.call?.integrationId?.toString()
        || this.softphoneService.selectedIntegrationId(),
      targetNumber: this.callLeadContext()?.leadPhone || this.dialerState().destination,
      startedAt: this.callState().mainStartedAt || Date.now(),
      source: this.callLeadContext()?.source || (this.activeTransfer() ? 'campaign-tasks' : 'manual'),
      progress: this.callProgress(),
    };
  }

  private async acquireWakeLock() {
    if (this.wakeLockSentinel || !this.wakeLockSupported || document.visibilityState !== 'visible') return;
    if (this.wakeLockRequest) return await this.wakeLockRequest;

    const generation = this.wakeLockGeneration;
    const request = (async () => {
      try {
        const sentinel = await (navigator as any).wakeLock.request('screen');
        if (generation !== this.wakeLockGeneration || document.visibilityState !== 'visible') {
          try { await sentinel.release(); } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'wake-lock-release-cleanup' }); }
          return;
        }
        this.wakeLockSentinel = sentinel;
        sentinel.addEventListener('release', () => {
          if (this.wakeLockSentinel === sentinel) this.wakeLockSentinel = undefined;
        });
      } catch (error) {
        this.log('wakeLock:acquire:failed', { error: this.summarizeError(error) }, 'warn', error);
      }
    })();
    this.wakeLockRequest = request;
    try {
      await request;
    } finally {
      if (this.wakeLockRequest === request) this.wakeLockRequest = undefined;
    }
  }

  private releaseWakeLock() {
    this.wakeLockGeneration += 1;
    try {
      void this.wakeLockSentinel?.release().catch((error: unknown) => this.exceptions.record(error, { source: 'softphone', operation: 'wake-lock-release-cleanup' }));
    } catch (error) { this.exceptions.record(error, { source: 'softphone', operation: 'wake-lock-release-cleanup' }); }
    this.wakeLockSentinel = undefined;
  }

  private async reacquireWakeLock() {
    if (!this.wakeLockSentinel) {
      await this.acquireWakeLock();
    }
  }

  private async destroyDevice(syncStatus = true) {
    for (const entry of this.qualityCalls.values()) entry.cleanup();
    this.qualityCalls.clear();
    this.clearCallQuality();
    this.beginRuntimeDestruction();
    this.releaseWakeLock();
    this.clearOwnerIncomingTransferMirrorState();
    this.clearOwnerPublishedActiveCallMirrorState();
    this.clearActiveTransfer('destroy-device');
    this.clearTransferContext('destroy-device');
    this.resetCallState();
    this.registeredDevice = undefined;

    await this.softphoneService.releaseSoftphoneDevice('softphone', true);
    this.device = undefined;
    this.finishRuntimeDestruction();

    if (syncStatus && this.user()?.agentInfo?.liveTransfer) {
      await this.softphoneService.publishOwnerRuntimeSnapshot('shutdown', { activeCall: null, enabled: this.enabled() });
    }
  }

  private prepareLeadCall(intent: SoftphoneCallLeadIntent) {
    const leadPhone = this.normalizeDialTarget(intent.leadPhone);
    this.callState.update(current => ({
      ...current,
      leadContext: {
        source: intent.source,
        leadName: intent.leadName,
        leadPhone,
        leadState: intent.leadState,
        preferredFromNumber: intent.preferredFromNumber,
        campaignId: intent.campaignId,
        previousCallId: intent.previousCallId,
      },
    }));

    this.updateDialDestination(leadPhone);
    this.openPanel();
    void this.resolveFromNumberForCurrentContext();
  }

  private async resolveFromNumberForCurrentContext(workspaceId?: string) {
    const currentContext = this.callLeadContext();
    if (!currentContext?.preferredFromNumber) return undefined;
    if (this.previewBlocked()) return undefined;
    if (currentContext.resolvedFromNumber) return currentContext.resolvedFromNumber;
    if (this.fromNumberResolutionPromise) return await this.fromNumberResolutionPromise;
    const context = currentContext;

    const resolutionPromise = (async () => {
      this.dialerState.update(current => ({ ...current, resolvingFromNumber: true }));
      const result = await this.softphoneService.resolveSoftphoneFromNumber({
        integrationId: this.softphoneService.selectedIntegrationId(),
        previousCallId: context!.previousCallId,
        preferredFromNumber: context!.preferredFromNumber,
        leadPhone: context!.leadPhone,
        leadState: context!.leadState,
      }, workspaceId);

      if (!result.resolvedFromNumber) {
        throw new Error(result.reason || 'No outbound caller ID is available.');
      }

      if (this.callLeadContext() !== context) return result.resolvedFromNumber;

      this.callState.update(current => {
        if (!current.leadContext || current.leadContext !== context) return current;
        return { ...current, leadContext: { ...current.leadContext, resolvedFromNumber: result.resolvedFromNumber } };
      });
      if (!result.isAvailable && result.replacementFromNumber) {
        const replacementMessage = `Caller ID ${result.requestedFromNumber || context.preferredFromNumber} was unavailable. Using ${result.replacementFromNumber}.`;
        this.callState.update(current => {
          if (!current.leadContext || current.leadContext !== context) return current;
          return { ...current, leadContext: { ...current.leadContext, replacementMessage } };
        });
        this.app.message$.next({
          severity: 'warning',
          summary: 'Soft phone',
          detail: replacementMessage,
        });
      } else {
        this.callState.update(current => {
          if (!current.leadContext || current.leadContext !== context) return current;
          return { ...current, leadContext: { ...current.leadContext, replacementMessage: undefined } };
        });
      }

      return result.resolvedFromNumber;
    })();
    this.fromNumberResolutionPromise = resolutionPromise;

    try {
      return await resolutionPromise;
    } finally {
      if (this.fromNumberResolutionPromise === resolutionPromise) {
        this.fromNumberResolutionPromise = undefined;
        this.dialerState.update(current => ({ ...current, resolvingFromNumber: false }));
      }
    }
  }

  private normalizeDialTarget(value: string) {
    const cleaned = value.trim();
    if (cleaned.startsWith('client:')) return cleaned;

    const digits = cleaned.replace(/[^\d+*#]/g, '');
    if (/^\d{10}$/.test(digits)) return `+1${digits}`;
    if (/^\d{11}$/.test(digits)) return `+${digits}`;
    return digits;
  }

  private coercePhoneInput(value: string) {
    const cleaned = value?.toString().trim() || '';
    if (cleaned.startsWith('client:')) return cleaned;

    const plusPrefix = cleaned.startsWith('+') ? '+' : '';
    const body = cleaned.replace(/[^\d*#]/g, '');
    return `${plusPrefix}${body}`;
  }

  private getCallSid(call?: TwilioCallLike) {
    return this.getCallParam(call, 'CallSid');
  }

  private getCallParam(call: TwilioCallLike | undefined, name: string) {
    const customParameters = (call as any)?.customParameters;
    const value = call?.parameters?.[name] || customParameters?.get?.(name) || customParameters?.[name];
    return typeof value === 'string' ? value.trim() : undefined;
  }

  private getActiveTransferId() {
    if (this.transferContext()?.incomingType === 'recovery') return undefined;
    const transfer = this.activeTransfer();
    return transfer?._id?.toString();
  }

  private clearActiveTransfer(reason: string) {
    const transfer = this.activeTransfer();
    if (transfer) {
      this.log('transfer:clear', this.buildTransferLogDetail(reason, { transfer }));
    }
    this.callState.update(current => ({ ...current, activeTransfer: undefined }));
  }

  private clearTransferContext(reason: string) {
    const transferContext = this.transferContext();
    if (transferContext) {
      this.log('transfer-context:clear', this.buildTransferLogDetail(reason, transferContext));
    }
    this.callState.update(current => ({ ...current, transferContext: undefined }));
  }

  private buildTransferLogDetail(
    reason: string,
    source: SoftphoneTransferContext | { transfer?: TransferActivity },
  ) {
    const detail: {
      reason: string;
      transferId?: string;
      callId?: string;
      campaignId?: string;
    } = { reason };

    const transferId = source.transfer?._id?.toString();
    if (transferId) detail.transferId = transferId;

    if ('call' in source) {
      const callId = source.call?._id?.toString();
      if (callId) detail.callId = callId;

      const campaignId = source.campaign?._id?.toString();
      if (campaignId) detail.campaignId = campaignId;
    }

    return detail;
  }

  private formatDuration(totalSeconds: number) {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private async loadOriginPreference(integrationId: string) {
    if (!this.canOriginPreferences()) return;
    const requestVersion = ++this.originPreferenceRequestVersion;
    const cachedPreference = this.softphoneStateDto()?.originPreferences?.find(preference => preference.integrationId === integrationId);
    if (!this.isCurrentOriginPreferenceRequest(integrationId, requestVersion)) return;
    this.dialerState.update(current => ({
      ...current,
      originPreference: cachedPreference || null,
      originMode: cachedPreference?.mode || 'local-presence',
      selectedOriginNumberId: cachedPreference?.phoneNumberId,
    }));

    if (cachedPreference?.mode === 'specific-number') {
      await this.loadOriginNumberOptionsForIntegration(integrationId, requestVersion);
    } else {
      if (!this.isCurrentOriginPreferenceRequest(integrationId, requestVersion)) return;
      this.dialerState.update(current => ({ ...current, originNumberOptions: [] }));
    }

    try {
      const preference = await this.softphoneService.getSoftphoneOriginPreference(integrationId);
      if (!this.isCurrentOriginPreferenceRequest(integrationId, requestVersion)) return;
      this.dialerState.update(current => ({
        ...current,
        originPreference: preference,
        originMode: preference?.mode || 'local-presence',
        selectedOriginNumberId: preference?.phoneNumberId,
      }));
      if (preference?.mode === 'specific-number') {
        await this.loadOriginNumberOptionsForIntegration(integrationId, requestVersion);
      } else {
        if (!this.isCurrentOriginPreferenceRequest(integrationId, requestVersion)) return;
        this.clearOriginPreview();
      }
      if (!this.isCurrentOriginPreferenceRequest(integrationId, requestVersion)) return;
      if (preference?.mode === 'specific-number') {
        await this.refreshCallerIdPreview();
      }
    } catch (error) {
      if (!this.isCurrentOriginPreferenceRequest(integrationId, requestVersion)) return;
      this.exceptions.record(error, { source: 'softphone', operation: 'caller-id-preference-load', integrationId });
      this.dialerState.update(current => ({ ...current, originPreference: cachedPreference || null }));
    }
  }

  private async refreshCallerIdPreview(targetNumber = this.callerIdPreviewTarget(), integrationId = this.selectedIntegrationId()) {
    const normalizedTarget = targetNumber?.trim();
    if (!normalizedTarget || !integrationId) {
      this.clearOriginPreview();
      return;
    }
    if (this.previewBlocked()) {
      this.clearOriginPreview();
      return;
    }

    const context = this.callLeadContext();
    const requestVersion = ++this.callerIdPreviewRequestVersion;
    this.beginOriginPreview();
    try {
      const result = await this.softphoneService.resolveSoftphoneFromNumber({
        integrationId,
        previousCallId: context?.previousCallId,
        preferredFromNumber: context?.preferredFromNumber,
        leadPhone: normalizedTarget,
        leadState: context?.leadState,
      });

      if (!this.isCurrentCallerIdPreviewRequest(integrationId, normalizedTarget, requestVersion)) return;

      this.finishOriginPreview({
        resolvedFromNumber: result.resolvedFromNumber,
        unavailableReason: result.resolvedFromNumber ? undefined : (result.reason || 'No outbound caller ID is available.'),
      });
    } catch (error: any) {
      if (!this.isCurrentCallerIdPreviewRequest(integrationId, normalizedTarget, requestVersion)) return;
      this.exceptions.record(error, { source: 'softphone', operation: 'caller-id-preview', integrationId });
      this.finishOriginPreview({
        unavailableReason: 'No outbound caller ID is available.',
      });
    } finally {
      if (requestVersion === this.callerIdPreviewRequestVersion) {
        this.dialerState.update(current => ({ ...current, loadingCallerIdPreview: false }));
      }
    }
  }

  private async loadOriginNumberOptionsForIntegration(integrationId: string, requestVersion?: number) {
    this.dialerState.update(current => ({ ...current, loadingOriginNumberOptions: true }));
    try {
      const options = await this.softphoneService.getSoftphoneOriginNumberOptions(integrationId, this.dialerState().originQuery);
      if (!this.isCurrentOriginNumberOptionsRequest(integrationId, requestVersion)) return;
      this.dialerState.update(current => ({ ...current, originNumberOptions: options }));
      if (this.dialerState().selectedOriginNumberId && !options.some(option => option.phoneNumberId === this.dialerState().selectedOriginNumberId)) {
        this.dialerState.update(current => ({ ...current, selectedOriginNumberId: undefined }));
      }
    } finally {
      if (this.shouldClearOriginNumberOptionsLoading(integrationId, requestVersion)) {
        this.dialerState.update(current => ({ ...current, loadingOriginNumberOptions: false }));
      }
    }
  }

  private isCurrentOriginPreferenceRequest(integrationId: string, requestVersion: number) {
    return this.isWorkspaceRoute()
      && this.selectedIntegrationId() === integrationId
      && this.originPreferenceRequestVersion === requestVersion;
  }

  private isCurrentOriginNumberOptionsRequest(integrationId: string, requestVersion?: number) {
    return this.isWorkspaceRoute()
      && this.selectedIntegrationId() === integrationId
      && (requestVersion === undefined || this.originPreferenceRequestVersion === requestVersion);
  }

  private shouldClearOriginNumberOptionsLoading(integrationId: string, requestVersion?: number) {
    return !this.isWorkspaceRoute()
      || this.selectedIntegrationId() !== integrationId
      || this.dialerState().originMode !== 'specific-number'
      || requestVersion === undefined
      || this.originPreferenceRequestVersion === requestVersion;
  }

  private isCurrentCallerIdPreviewRequest(integrationId: string, normalizedTarget: string, requestVersion: number) {
    return this.isWorkspaceRoute()
      && this.selectedIntegrationId() === integrationId
      && this.callerIdPreviewTarget()?.trim() === normalizedTarget
      && this.callerIdPreviewRequestVersion === requestVersion;
  }

  private async syncActiveTransferForCall(call: TwilioCallLike | undefined, source: string) {
    const transferId = this.getCallParam(call, 'TransferId');
    if (!transferId) {
      this.clearActiveTransfer(`missing-transfer-id:${source}`);
      return;
    }

    try {
      const activity = await this.activityService.getById(transferId);
      const transfer = activity?.type === 'transfer' ? activity as TransferActivity : undefined;

      const currentTransferId = this.getCallParam(this.activeCall() || this.incomingCall(), 'TransferId');
      if (currentTransferId !== transferId) return;

      this.callState.update(current => ({ ...current, activeTransfer: transfer }));
      this.hydrateTransferContextFromTransfer(transfer);
      this.log('transfer:sync', { source, transferId, transfer, found: !!transfer });
    } catch (error: any) {
      this.log('transfer:sync:error', { source, transferId, error }, 'warn');
      this.callState.update(current => ({ ...current, activeTransfer: undefined }));
    }
  }

  private setTransferContextFromIncomingPayload(payload: IncomingCallPayload) {
    this.callState.update(current => ({
      ...current,
      transferContext: {
        incomingType: payload.type,
        handoff: payload.handoff,
        call: payload.call,
        campaign: payload.campaign,
        transfer: payload.transfer,
      },
    }));
    this.refreshQualityContexts();
  }

  private applyMirroredIncomingTransfer(incomingTransfer?: SoftphoneIncomingTransferSummary) {
    if (!incomingTransfer) {
      this.clearMirroredIncomingTransferTimeout();
      if (this.mirroredIncomingTransfer() || this.incomingTransferNotice()) {
        this.callState.update(current => ({ ...current, mirroredIncoming: undefined, incomingNotice: undefined }));
      }
      return;
    }
    if (this.softphoneService.isIncomingTransferCompleted(this.getIncomingTransferKeys(incomingTransfer))) {
      this.clearIncomingTransferState(incomingTransfer, undefined, false);
      return;
    }
    const ownsIncomingTransferLifecycle = this.softphoneService.isOwnerTab;
    if (ownsIncomingTransferLifecycle) {
      this.scheduleMirroredIncomingTransferExpiry(incomingTransfer);
    }
    if (incomingTransfer.noticeTabId && this.softphoneService.isCurrentTab(incomingTransfer.noticeTabId)) {
      this.scheduleMirroredIncomingTransferExpiry(incomingTransfer);
      this.callState.update(current => ({ ...current, incomingNotice: incomingTransfer, mirroredIncoming: undefined }));
      this.openPanel();
      return;
    }
    this.callState.update(current => ({ ...current, incomingNotice: undefined }));
    if (!incomingTransfer.targetTabId || !this.softphoneService.isCurrentTab(incomingTransfer.targetTabId) || this.incomingCall()) {
      if (!ownsIncomingTransferLifecycle) this.clearMirroredIncomingTransferTimeout();
      this.callState.update(current => ({ ...current, mirroredIncoming: undefined }));
      return;
    }

    this.scheduleMirroredIncomingTransferExpiry(incomingTransfer);
    this.callState.update(current => ({ ...current, mirroredIncoming: incomingTransfer }));
    this.mergeMirroredIncomingTransferContext(incomingTransfer);
    this.openPanel();
  }

  private captureOwnerIncomingTransferContext(
    payload: IncomingCallPayload,
    workspaceId?: string,
    targetTabId?: string,
    noticeTabId?: string,
  ) {
    if (!this.softphoneService.isOwnerTab) return;
    const transferId = payload.transfer?._id?.toString();
    const currentTransferId = this.ownerIncomingTransferContext?.transfer?._id?.toString();
    const currentCallId = this.ownerIncomingTransferContext?.call?._id?.toString();
    const payloadCallId = payload.call?._id?.toString();
    const matchesCurrentContext = payload.type === 'recovery'
      ? !!payloadCallId && payloadCallId === currentCallId
      : !!transferId && transferId === currentTransferId;
    const currentTarget = matchesCurrentContext ? this.ownerIncomingTransferContext : undefined;
    const fallbackTarget = this.incomingFallback?.shown
      && this.incomingFallback.call === this.ownerIncomingCall ? this.sessionState().tabId : undefined;
    this.ownerIncomingTransferContext = {
      workspaceId,
      targetTabId: fallbackTarget || currentTarget?.targetTabId || targetTabId,
      noticeTabId: fallbackTarget ? undefined : currentTarget?.noticeTabId || noticeTabId,
      incomingType: payload.type,
      handoff: payload.handoff,
      call: payload.call,
      campaign: payload.campaign,
      transfer: payload.transfer,
    };
  }

  private mergeMirroredIncomingTransferContext(incomingTransfer: SoftphoneIncomingTransferSummary) {
    const existingContext = this.transferContext();
    const nextContext: SoftphoneTransferContext = {
      incomingType: incomingTransfer.incomingType ?? existingContext?.incomingType,
      handoff: incomingTransfer.handoff,
      call: incomingTransfer.call ?? existingContext?.call,
      campaign: incomingTransfer.campaign ?? existingContext?.campaign,
      transfer: incomingTransfer.transfer ?? existingContext?.transfer,
    };
    if (!nextContext.call && !nextContext.campaign && !nextContext.transfer) return;
    this.callState.update(current => ({ ...current, transferContext: nextContext }));
  }

  private buildOwnerIncomingTransferSummary(): SoftphoneIncomingTransferSummary | undefined {
    const ownerContext = this.ownerIncomingTransferContext;
    const currentContext = this.transferContext();
    const nextTargetTabId = ownerContext?.targetTabId;
    if (!nextTargetTabId) return undefined;
    return {
      targetTabId: nextTargetTabId,
      noticeTabId: ownerContext?.noticeTabId,
      incomingType: ownerContext?.incomingType ?? currentContext?.incomingType,
      handoff: ownerContext ? ownerContext.handoff : currentContext?.handoff,
      call: ownerContext?.call ?? currentContext?.call,
      campaign: ownerContext?.campaign ?? currentContext?.campaign,
      transfer: ownerContext?.transfer ?? currentContext?.transfer,
    };
  }

  private clearIncomingFallback() {
    if (this.incomingFallback?.timer !== undefined) clearTimeout(this.incomingFallback.timer);
    if (this.incomingFallback?.shown && this.incomingCall() === this.incomingFallback.call) {
      this.incomingCall.set(undefined);
      if (this.ownerIncomingCall === this.incomingFallback.call) this.ownerIncomingCall = undefined;
      this.callState.update(current => ({ ...current, incoming: undefined }));
    }
    this.incomingFallback = undefined;
  }

  private incomingFallbackBlockedReason(call: TwilioCallLike) {
    if (this.ownerIncomingCall !== call) return 'call-replaced';
    if (!this.softphoneService.isOwnerTab || this.browserSessionMode() !== 'active') return 'ownership-lost';
    if (call.status() !== 'pending') return 'call-not-pending';
    if (this.activeCall()) return 'active-call';
    const callKeys = this.getIncomingTransferKeys(undefined, call);
    if (this.softphoneService.isIncomingTransferCompleted(callKeys)) return 'transfer-completed';
    const contextKeys = this.getIncomingTransferKeys(this.ownerIncomingTransferContext);
    if (contextKeys.length && (!callKeys.length
      || !this.softphoneService.incomingTransferIdentitiesOverlap(contextKeys, callKeys))) return 'identity-mismatch';
    return undefined;
  }

  private async recoverIncomingTransferContext(call: TwilioCallLike) {
    const callId = this.getCallParam(call, 'CallId');
    const transferId = this.getCallParam(call, 'TransferId');
    const integrationId = this.getCallParam(call, 'IntegrationId');
    const userId = this.user()?._id?.toString();
    const current = () => !!userId && userId === this.user()?._id?.toString()
      && !this.ownerIncomingTransferContext && !this.incomingFallbackBlockedReason(call);
    if (!callId || !transferId || !integrationId || !current()) return;
    try {
      const activity = await this.activityService.getById(transferId);
      if (!current() || activity?.type !== 'transfer') return;
      const transfer = activity as TransferActivity;
      if (transfer._id?.toString() !== transferId || transfer.callId?.toString() !== callId
        || transfer.agentType !== 'agent' || transfer.agentId?.toString() !== userId
        || transfer.integrationId?.toString() !== integrationId || !transfer.to?.startsWith('client:')
        || transfer.status !== 'initiated' || transfer.events?.terminalAt) return;
      const savedCall = await this.callService.getCall(callId) as FullCall;
      if (!current() || savedCall?._id?.toString() !== callId
        || savedCall.workspaceId?.toString() !== transfer.workspaceId?.toString()
        || savedCall.integrationId?.toString() !== integrationId
        || savedCall.campaignId?.toString() !== transfer.campaignId?.toString()
        || savedCall.hybridTransition?.mode !== 'transfer'
        || ['ended', 'completed', 'failed', 'canceled', 'busy', 'no-answer'].includes(savedCall.status || '')) return;
      const campaign = await this.campaignService.getById(savedCall.campaignId!);
      if (!current() || campaign?._id?.toString() !== savedCall.campaignId?.toString()
        || campaign.workspaceId?.toString() !== savedCall.workspaceId?.toString()
        || campaign.callCriteria?.provider !== 'DIALER') return;
      const payload: IncomingCallPayload = {
        type: 'transfer', call: savedCall as IncomingCallPayload['call'], campaign, transfer,
        handoff: { classification: savedCall.voxrDiagnostics?.hybrid?.classification, transcript: savedCall.transcript },
      };
      const workspaceId = savedCall.workspaceId?.toString();
      const tabs = this.softphoneService.selectIncomingTransferTabs(workspaceId);
      this.captureOwnerIncomingTransferContext(payload, workspaceId, tabs.targetTabId, tabs.noticeTabId);
      this.setTransferContextFromIncomingPayload(payload);
      this.reconcileOwnerIncomingTransfer();
      this.log('incoming-transfer:context-recovered', { callId, transferId });
    } catch (error) {
      this.log('incoming-transfer:context-recovery-failed', { callId, transferId, error }, 'warn');
    }
  }

  private scheduleIncomingFallback(call: TwilioCallLike) {
    if (this.incomingFallback?.call === call) return;
    this.clearIncomingFallback();
    this.incomingFallback = { call, startedAt: Date.now(), shown: false };
    const fallback = this.incomingFallback;
    fallback.timer = setTimeout(() => {
      if (this.incomingFallback !== fallback) return;
      fallback.timer = undefined;
      const reason = this.incomingFallbackBlockedReason(call);
      const detail = {
        callId: this.getCallParam(call, 'CallId'),
        transferId: this.getCallParam(call, 'TransferId'),
        elapsedMs: Date.now() - fallback.startedAt,
      };
      if (reason || this.ownerIncomingTransferContext?.targetTabId) {
        this.log('incoming-transfer:fallback-skipped', { ...detail, reason: reason || 'context-available' });
        return;
      }
      fallback.shown = true;
      this.beginIncomingCall(call);
      this.expandPanel();
      this.openPanel();
      this.log('incoming-transfer:fallback-shown', detail);
      void this.recoverIncomingTransferContext(call);
    }, 500);
  }

  private allowIncomingFallbackAction(call?: TwilioCallLike) {
    const fallback = this.incomingFallback;
    if (!fallback?.shown) return true;
    if (fallback.actionTaken) return false;
    if (call !== fallback.call || this.incomingFallbackBlockedReason(fallback.call)) {
      this.incomingCall.set(undefined);
      this.callState.update(current => ({ ...current, incoming: undefined }));
      this.handleUnavailableIncomingTransfer(true);
      return false;
    }
    fallback.actionTaken = true;
    return true;
  }

  private cancelIncomingAutoAnswer(call?: TwilioCallLike) {
    if (call && this.incomingAutoAnswer?.call !== call) return;
    const pending = this.incomingAutoAnswer;
    this.incomingAutoAnswer = undefined;
    pending?.cancelBeep?.();
  }

  private async tryAutoAnswerIncoming() {
    const pending = this.incomingAutoAnswer;
    if (!pending || pending.reason || pending.handled) return;
    const { call, mode } = pending;
    if (!this.enabled() || this.contractVersionMismatch() || this.incomingFallbackBlockedReason(call)) return;
    const context = this.ownerIncomingTransferContext;
    const callKeys = this.getIncomingTransferKeys(undefined, call);
    const contextKeys = this.getIncomingTransferKeys(context);
    if (context && (!contextKeys.length || !callKeys.length
      || !this.softphoneService.incomingTransferIdentitiesOverlap(contextKeys, callKeys))) return;
    const callId = this.getCallParam(call, 'CallId');
    const transferId = this.getCallParam(call, 'TransferId');
    if (context && ((callId && callId !== context.call?._id?.toString())
      || (transferId && context.incomingType !== 'recovery' && transferId !== context.transfer?._id?.toString()))) return;
    if (mode === 'campaign' && !context) return;
    const reason = mode === 'all' ? 'all-calls'
      : context?.incomingType !== 'transfer' ? 'manual'
      : context.campaign?.callCriteria?.provider === 'DIALER' ? 'transfer-hybrid'
      : context.campaign?.transferCriteria?.softphoneAutoAnswer === true ? 'campaign-required' : 'manual';
    pending.reason = reason;
    this.log('auto-answer:decision', { mode, reason });
    if (reason === 'manual') return;

    const beep = await this.playAutoAnswerBeep(pending);
    this.log('auto-answer:beep', { mode, outcome: beep });
    if (beep === 'canceled' || this.incomingAutoAnswer !== pending || pending.handled
      || pending.userId !== this.user()?._id?.toString() || !this.enabled() || this.contractVersionMismatch()
      || this.incomingFallbackBlockedReason(call)) return;
    // Do not accept a replacement context while the warning sound is playing.
    const currentKeys = this.getIncomingTransferKeys(this.ownerIncomingTransferContext);
    if (currentKeys.length && !this.softphoneService.incomingTransferIdentitiesOverlap(currentKeys, callKeys)) return;
    const currentContext = this.ownerIncomingTransferContext;
    if (currentContext && ((callId && callId !== currentContext.call?._id?.toString())
      || (transferId && currentContext.incomingType !== 'recovery' && transferId !== currentContext.transfer?._id?.toString()))) return;
    if (beep === 'failed') {
      this.app.message$.next({ severity: 'warning', summary: 'Auto answer', detail: 'The call will be answered, but the warning beep could not play. Check your browser audio settings.' });
    }
    try {
      this.acceptIncoming(true);
    } catch (error) {
      pending.handled = false;
      if (this.incomingFallback?.call === call) this.incomingFallback.actionTaken = false;
      this.log('auto-answer:failed', { mode }, 'warn', error);
      this.app.message$.next({ severity: 'warning', summary: 'Auto answer', detail: 'The call could not be answered automatically. Try answering it manually.' });
    }
  }

  private playAutoAnswerBeep(pending: IncomingAutoAnswer): Promise<'played' | 'failed' | 'canceled'> {
    return new Promise(resolve => {
      let audio: AudioContext | undefined;
      let finished = false;
      const finish = (outcome: 'played' | 'failed' | 'canceled') => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        pending.cancelBeep = undefined;
        if (audio) void audio.close().catch(() => undefined);
        resolve(outcome);
      };
      const timeout = setTimeout(() => finish('failed'), 500);
      pending.cancelBeep = () => finish('canceled');
      try {
        audio = new AudioContext();
        const context = audio;
        void context.resume().then(() => {
          if (finished) return;
          if (context.state !== 'running') { finish('failed'); return; }
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          oscillator.frequency.value = 880;
          gain.gain.setValueAtTime(0.1, context.currentTime);
          gain.gain.linearRampToValueAtTime(0, context.currentTime + 0.2);
          oscillator.connect(gain);
          gain.connect(context.destination);
          oscillator.onended = () => finish('played');
          oscillator.start();
          oscillator.stop(context.currentTime + 0.2);
        }).catch(() => finish('failed'));
      } catch {
        finish('failed');
      }
    });
  }

  private reconcileOwnerIncomingTransfer() {
    if (!this.softphoneService.isOwnerTab) return;
    const call = this.ownerIncomingCall;
    const ownerContext = this.ownerIncomingTransferContext;
    if (!call) {
      this.logIncomingTransferReconciliation('missing-twilio-call', call, ownerContext);
      return;
    }
    if (!ownerContext?.targetTabId) {
      this.logIncomingTransferReconciliation('missing-transfer-context', call, ownerContext);
      return;
    }
    if (this.reconciledOwnerIncomingCall === call) {
      this.logIncomingTransferReconciliation('already-reconciled', call, ownerContext);
      return;
    }

    const contextKeys = this.getIncomingTransferKeys(ownerContext);
    const callKeys = this.getIncomingTransferKeys(undefined, call);
    if (contextKeys.length && callKeys.length
      && !this.softphoneService.incomingTransferIdentitiesOverlap(contextKeys, callKeys)) {
      this.logIncomingTransferReconciliation('identity-mismatch', call, ownerContext);
      return;
    }

    const identityKeys = [...new Set([...contextKeys, ...callKeys])];
    if (this.softphoneService.isIncomingTransferCompleted(identityKeys)) {
      this.logIncomingTransferReconciliation('transfer-completed', call, ownerContext);
      return;
    }

    const fallbackShown = this.incomingFallback?.call === call && this.incomingFallback.shown;
    if (!fallbackShown) this.clearIncomingFallback();
    this.reconciledOwnerIncomingCall = call;
    this.softphoneService.publishIncomingTransfer(this.buildOwnerIncomingTransferSummary());
    void this.syncIncomingTransferContextAndRepublish(call);
    void this.tryAutoAnswerIncoming();
    if (!this.softphoneService.isCurrentTab(ownerContext.targetTabId)) {
      this.logIncomingTransferReconciliation('selected-for-another-tab', call, ownerContext);
      return;
    }

    if (!fallbackShown) {
      this.beginIncomingCall(call);
      this.openPanel();
    }
    this.logIncomingTransferReconciliation('actionable-popup-opened', call, ownerContext);
  }

  private logIncomingTransferTwilioReceived(call: TwilioCallLike, source: string, integrationId?: string) {
    this.log('incoming-transfer:twilio-received', {
      source,
      transferId: this.getCallParam(call, 'TransferId'),
      callId: this.getCallParam(call, 'CallId'),
      integrationId: integrationId || this.getCallParam(call, 'IntegrationId'),
      callSid: this.getCallSid(call),
      isOwner: this.softphoneService.isOwnerTab,
      callProgress: this.callProgress(),
    });
  }

  private logIncomingTransferReconciliation(
    reason: string,
    call = this.ownerIncomingCall,
    ownerContext = this.ownerIncomingTransferContext,
  ) {
    this.log('incoming-transfer:reconcile', {
      reason,
      transferId: ownerContext?.transfer?._id?.toString() || this.getCallParam(call, 'TransferId'),
      callId: ownerContext?.call?._id?.toString() || ownerContext?.transfer?.callId?.toString() || this.getCallParam(call, 'CallId'),
      integrationId: ownerContext?.transfer?.integrationId?.toString()
        || ownerContext?.call?.integrationId?.toString()
        || this.getCallParam(call, 'IntegrationId'),
      workspaceId: ownerContext?.workspaceId,
      callSid: this.getCallSid(call),
      isOwner: this.softphoneService.isOwnerTab,
      hasTwilioCall: !!call,
      hasTransferContext: !!ownerContext?.targetTabId,
      ownerIsTarget: !!ownerContext?.targetTabId && this.softphoneService.isCurrentTab(ownerContext.targetTabId),
      ownerIsNotice: !!ownerContext?.noticeTabId && this.softphoneService.isCurrentTab(ownerContext.noticeTabId),
      callProgress: this.callProgress(),
    });
  }

  private async syncIncomingTransferContextAndRepublish(call: TwilioCallLike) {
    await this.syncActiveTransferForCall(call, 'incoming');
    if (!this.softphoneService.isOwnerTab || this.ownerIncomingCall !== call) return;
    this.softphoneService.publishIncomingTransfer(this.buildOwnerIncomingTransferSummary());
  }

  private clearMirroredCallState(call?: TwilioCallLike, incoming = false) {
    this.cancelIncomingAutoAnswer(call);
    if (!call || this.ownerIncomingCall === call) this.ownerIncomingCall = undefined;
    if (!call || this.reconciledOwnerIncomingCall === call) this.reconciledOwnerIncomingCall = undefined;
    if (incoming) this.clearIncomingTransferState(undefined, call);
    this.clearOwnerPublishedActiveCallMirrorState();
  }

  private clearOwnerIncomingTransferMirrorState() {
    this.clearIncomingTransferState();
  }

  private clearIncomingTransferState(
    expectedTransfer?: SoftphoneTransferContext,
    expectedCall?: TwilioCallLike,
    markCompleted = true,
  ) {
    const expectedKeys = this.getIncomingTransferKeys(expectedTransfer, expectedCall);
    const currentKeys = this.getCurrentIncomingTransferKeys();
    if (expectedKeys.length && currentKeys.length
      && !this.softphoneService.incomingTransferIdentitiesOverlap(expectedKeys, currentKeys)) return false;

    this.cancelIncomingAutoAnswer(expectedCall);
    const completedKeys = [...new Set([...currentKeys, ...expectedKeys])];
    if (markCompleted) this.softphoneService.completeIncomingTransfer(completedKeys);
    this.clearMirroredIncomingTransferTimeout();
    this.clearIncomingFallback();
    this.ownerIncomingCall = undefined;
    this.reconciledOwnerIncomingCall = undefined;
    this.ownerIncomingTransferContext = undefined;
    this.callState.update(current => ({ ...current, mirroredIncoming: undefined, incomingNotice: undefined }));
    if (this.softphoneService.isOwnerTab) {
      this.softphoneService.publishIncomingTransfer(undefined);
    }
    return true;
  }

  private scheduleMirroredIncomingTransferExpiry(incomingTransfer: SoftphoneIncomingTransferSummary) {
    const key = this.getIncomingTransferKeys(incomingTransfer)[0]
      || `${incomingTransfer.targetTabId || ''}:${incomingTransfer.noticeTabId || ''}`;
    if (this.mirroredIncomingTransferKey === key && this.mirroredIncomingTransferTimeout) return;
    this.clearMirroredIncomingTransferTimeout();
    this.mirroredIncomingTransferKey = key;
    this.mirroredIncomingTransferTimeout = setTimeout(() => {
      this.clearIncomingTransferState(incomingTransfer);
    }, 60_000);
  }

  private clearMirroredIncomingTransferTimeout() {
    if (this.mirroredIncomingTransferTimeout) clearTimeout(this.mirroredIncomingTransferTimeout);
    this.mirroredIncomingTransferTimeout = undefined;
    this.mirroredIncomingTransferKey = undefined;
  }

  private handleUnavailableIncomingTransfer(fallbackCall = false) {
    this.clearIncomingTransferState(this.mirroredIncomingTransfer());
    this.softphoneService.publishSoftphoneState(
      'loading',
      fallbackCall ? 'This call is no longer available.' : 'The calling connection in another VOXR tab was lost. VOXR is reconnecting.',
    );
    this.app.message$.next({
      severity: 'warning',
      summary: 'Soft phone',
      detail: fallbackCall ? 'This call is no longer available.' : 'This transfer is no longer available. VOXR is reconnecting.',
    });
  }

  private clearOwnerPublishedActiveCallMirrorState() {
    if (this.softphoneService.isOwnerTab) {
      this.softphoneService.publishActiveCall(undefined);
    }
  }

  private hasUnloadProtectedCallState() {
    return (!!this.activeCall() && this.callProgress() !== 'ended' && this.callProgress() !== 'idle')
      || !!this.incomingCall()
      || !!this.ownerIncomingCall
      || !!this.mirroredIncomingTransfer()
      || !!this.device?.isBusy
      || !!(this.device as any)?.calls?.length;
  }

  private getIncomingPayloadWorkspaceId(payload: IncomingCallPayload) {
    const call = payload.call as any;
    const campaign = payload.campaign as any;
    const transfer = payload.transfer as any;
    return call?.workspaceId?.toString?.()
      || campaign?.workspaceId?.toString?.()
      || transfer?.workspaceId?.toString?.()
      || transfer?.campaign?.workspaceId?.toString?.();
  }

  private shouldAdoptIncomingTransferPayload(payload: IncomingCallPayload) {
    if (this.incomingFallback?.shown) {
      const payloadKeys = this.getIncomingTransferKeys(payload);
      const callKeys = this.getIncomingTransferKeys(undefined, this.incomingFallback.call);
      if (!payloadKeys.length || !callKeys.length
        || !this.softphoneService.incomingTransferIdentitiesOverlap(payloadKeys, callKeys)) return false;
    }
    if (this.softphoneService.isIncomingTransferCompleted(this.getIncomingTransferKeys(payload))) return false;
    const payloadTransferId = payload.transfer?._id?.toString();
    const payloadCallId = payload.call?._id?.toString();
    if (payload.type === 'recovery') {
      const trackedCall = this.activeCall() || this.incomingCall() || this.ownerIncomingCall;
      if (!trackedCall) return true;
      return !!payloadCallId && payloadCallId === this.getCallParam(trackedCall, 'CallId');
    }
    if (this.transferContext()?.incomingType === 'recovery'
      && (this.activeCall() || this.incomingCall() || this.ownerIncomingCall)) return false;
    const trackedTransferId = this.activeTransfer()?._id?.toString()
      || this.transferContext()?.transfer?._id?.toString()
      || this.getCallParam(this.incomingCall(), 'TransferId')
      || this.getCallParam(this.activeCall(), 'TransferId');

    if (!this.activeCall()) return true;
    if (!payloadTransferId || !trackedTransferId) return false;
    return payloadTransferId === trackedTransferId;
  }

  private getIncomingTransferKeys(
    transfer?: SoftphoneTransferContext | IncomingCallPayload,
    call?: TwilioCallLike,
  ) {
    const incomingType = transfer && 'type' in transfer ? transfer.type : transfer?.incomingType;
    return this.softphoneService.getIncomingTransferIdentityKeys({
      transferId: incomingType === 'recovery'
        ? undefined
        : transfer?.transfer?._id?.toString() || this.getCallParam(call, 'TransferId'),
      callId: transfer?.call?._id?.toString()
        || (incomingType === 'recovery' ? undefined : transfer?.transfer?.callId?.toString())
        || this.getCallParam(call, 'CallId'),
    });
  }

  private getCurrentIncomingTransferKeys() {
    const candidates = [
      this.getIncomingTransferKeys(this.ownerIncomingTransferContext, this.ownerIncomingCall),
      this.getIncomingTransferKeys(this.mirroredIncomingTransfer()),
      this.getIncomingTransferKeys(this.incomingTransferNotice()),
      this.getIncomingTransferKeys(this.sessionState().incomingTransfer),
    ];
    return candidates.find(keys => keys.length) || [];
  }

  private hydrateTransferContextFromTransfer(transfer?: TransferActivity) {
    if (!transfer) return;

    const existingContext = this.transferContext();
    if (existingContext?.transfer?._id?.toString() === transfer._id?.toString()) {
      this.callState.update(current => ({ ...current, transferContext: { ...existingContext, transfer } }));
      return;
    }

    const metadata = (transfer._metadata as any) || {};
    if (!metadata?.campaign && !metadata?.lead) return;

    const leadPhone = metadata?.lead?.phone;
    this.callState.update(current => ({ ...current, transferContext: {
      call: existingContext?.call || (leadPhone ? {
        flow: 'OUTBOUND',
        toNumber: leadPhone,
        summary: undefined,
        contactInfo: {
          firstName: metadata?.lead?.name || undefined,
          lastName: undefined,
          phone: leadPhone,
        }
      } as CallInterface : undefined),
      campaign: existingContext?.campaign || (metadata?.campaign ? { name: metadata.campaign.name } as CampaignInterface : undefined),
      transfer,
    } }));
  }

  private toErrorDiagnostic(error: any) {
    if (error == null) {
      return { errorType: String(error), errorString: this.getDiagnostics().sanitize(String(error)) };
    }
    if (typeof error === 'string' || typeof error === 'number' || typeof error === 'boolean') {
      return { errorType: typeof error, errorString: this.getDiagnostics().sanitize(String(error)) };
    }
    return this.getDiagnostics().sanitize(error);
  }

  private summarizeError(error: any) {
    const diagnostic = this.toErrorDiagnostic(error) as any;
    if (!diagnostic || typeof diagnostic !== 'object') return diagnostic;

    const cause = diagnostic.cause && typeof diagnostic.cause === 'object'
      ? this.toErrorDiagnostic(diagnostic.cause)
      : diagnostic.cause;

    return {
      name: diagnostic.name,
      message: diagnostic.message,
      stage: diagnostic.stage,
      code: diagnostic.code,
      status: diagnostic.status,
      didFetchCredential: diagnostic.didFetchCredential,
      didCreateDevice: diagnostic.didCreateDevice,
      didRegisterDevice: diagnostic.didRegisterDevice,
      cause,
    };
  }

  private log(event: string, detail?: any, level: SoftphoneDiagnosticLevel = 'info', originalError?: unknown) {
    if (detail && Object.prototype.hasOwnProperty.call(detail, 'error')) {
      this.exceptions.record(arguments.length >= 4 ? originalError : detail.error, {
        source: 'softphone', operation: event, stage: detail.stage,
        callId: detail.callId, transferId: detail.transferId,
        integrationId: detail.integrationId,
      });
    }
    this.getDiagnostics().record(event, detail, level);
    this.syncDiagnosticsState();
  }

  private getDiagnostics() {
    this.diagnostics ||= new SoftphoneDiagnostics();
    return this.diagnostics;
  }

  private syncDiagnosticsState() {
    const metadata = this.getDiagnostics().metadata;
    this.diagnosticsState.set({
      lastTokenFetchAt: metadata.lastTokenFetchAt,
      reconnectCount: metadata.reconnectAttempts,
      recentSdkEvents: metadata.lastSdkEvents,
    });
  }

  private updateDiagnosticTokenFetchAt(lastTokenFetchAt?: string) {
    this.getDiagnostics().setLastTokenFetchAt(lastTokenFetchAt);
    this.syncDiagnosticsState();
  }

  private qualityCallContext(call: TwilioCallLike, device = this.qualityCalls.get(call)?.device || this.device) {
    const stream = call.getLocalStream?.();
    const track = stream?.getAudioTracks?.()[0];
    const settings = track?.getSettings?.();
    const connection = (navigator as any).connection;
    const audio = device?.audio;
    const callId = this.getCallParam(call, 'CallId');
    const transferId = this.getCallParam(call, 'TransferId');
    const context = this.transferContext();
    const contextTransferId = context?.transfer?._id?.toString();
    const matchingContext = callId && context?.call?._id?.toString() === callId
      && (!transferId || !contextTransferId || transferId === contextTransferId) ? context : undefined;
    return { sdkVersion: (device?.constructor as typeof Device)?.version, microphonePermission: this.softphoneService.microphonePermission,
      userAgent: navigator.userAgent, language: navigator.language, platform: navigator.platform, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      connection: connection ? { effectiveType: connection.effectiveType, downlink: connection.downlink, rtt: connection.rtt, saveData: connection.saveData } : undefined,
      track: track ? { enabled: track.enabled, muted: track.muted, readyState: track.readyState, settings: settings ? {
        echoCancellation: settings.echoCancellation, noiseSuppression: settings.noiseSuppression, autoGainControl: settings.autoGainControl,
        sampleRate: settings.sampleRate, channelCount: settings.channelCount, latency: (settings as any).latency } : undefined } : undefined,
      devices: audio ? { inputCount: audio.availableInputDevices.size, outputCount: audio.availableOutputDevices.size,
        defaultInput: audio.inputDevice?.deviceId === 'default', defaultOutput: [...audio.speakerDevices.get()].some(d => d.deviceId === 'default') } : undefined,
      callSid: this.getCallSid(call), callId,
      transferId, integrationId: this.getCallParam(call, 'IntegrationId'),
      campaignId: this.getCallParam(call, 'CampaignId') || matchingContext?.campaign?._id?.toString(),
      workspaceId: this.getCallParam(call, 'WorkspaceId') || matchingContext?.call?.workspaceId?.toString(),
      online: navigator.onLine, visibility: document.visibilityState, mute: call.isMuted?.(), contractVersion: SOFTPHONE_CONTRACT_VERSION };
  }

  private refreshQualityContexts() {
    for (const [call, entry] of this.qualityCalls) {
      try { this.getDiagnostics().updateCapture(entry.id, this.qualityCallContext(call, entry.device)); } catch { /* Optional context unavailable. */ }
    }
  }

  private releaseLiveQualityTracking() {
    this.cancelAutomaticReport();
    for (const entry of this.qualityCalls.values()) {
      try { entry.cleanup(); } catch { /* Continue releasing other calls. */ }
    }
    this.qualityCalls.clear();
    for (const [device, observation] of this.qualityDevices || []) {
      try { device.audio?.off?.('deviceChange', observation.listener); } catch { /* Device teardown is best effort. */ }
    }
    this.qualityDevices?.clear();
    this.getDiagnostics().discardActiveCaptures();
    this.clearCallQuality();
  }

  private finishQualityCall(call: TwilioCallLike, outcome: string) {
    const entry = this.qualityCalls.get(call);
    if (!entry) return;
    try { this.getDiagnostics().updateCapture(entry.id, this.qualityCallContext(call)); } catch { /* Optional evidence must not stop call cleanup. */ }
    try { entry.cleanup(); } catch { /* SDK teardown is best effort. */ } this.qualityCalls.delete(call);
    const controllingCall = this.activeCall?.() || this.incomingCall?.();
    if (!controllingCall || controllingCall === call) this.clearCallQuality();
    this.completeQualityAttempt(entry.id, outcome);
  }

  /** Returns undefined when acceptance cannot be established, so callers keep the existing behavior. */
  private callWasAccepted(call: TwilioCallLike) {
    const entry = this.qualityCalls.get(call);
    const context = entry ? this.getDiagnostics().captureContext(entry.id) : undefined;
    return context ? !!context['acceptedAt'] : undefined;
  }

  private setLocalEnd(value: boolean, call = this.activeCall()) {
    const entry = call ? this.qualityCalls.get(call) : undefined;
    if (entry) entry.localEnd = value;
  }

  private markConnectionIssue(call: TwilioCallLike, active: boolean) {
    const entry = this.qualityCalls.get(call);
    if (!entry) return;
    entry.issueActive = active;
    if (active) entry.issueAt = Date.now();
  }

  private endedByNetwork(call: TwilioCallLike) {
    const entry = this.qualityCalls.get(call);
    return !!entry && (!!entry.issueActive
      || (entry.issueAt !== undefined && Date.now() - entry.issueAt <= this.networkDropWindowMs));
  }

  /** Maps an underlying SIP final response (Twilio 31000 + SIP status) to a confirmed dial outcome. */
  private confirmedDialOutcome(error: any) {
    switch (Number(error?.originalError?.code)) {
      case 31486: case 31600: return { kind: 'busy', message: 'The line was busy.' };
      case 31603: return { kind: 'declined', message: 'The call was declined.' };
      case 31480: case 31408: return { kind: 'no-answer', message: 'The call was not answered.' };
      default: return undefined;
    }
  }

  private explainRemoteCallEnd(call: TwilioCallLike) {
    if (this.callWasAccepted(call) !== true) return;
    if (this.qualityCalls.get(call)?.localEnd) {
      this.log('call:ended:classified', { kind: 'local-hangup' });
      return;
    }
    const controllingCall = this.activeCall() || this.incomingCall();
    if ((controllingCall && controllingCall !== call) || !this.enabled() || this.runtimeState().destroying) return;
    const networkDrop = this.endedByNetwork(call);
    this.log('call:ended:classified', { kind: networkDrop ? 'network-drop' : 'remote-hangup' });
    this.app.message$.next(networkDrop
      ? { severity: 'warning', summary: 'Soft phone', detail: this.networkDropMessage }
      : { severity: 'info', summary: 'Soft phone', detail: 'The call was ended from the other side.' });
  }

  private isLiveQualityCall(call: TwilioCallLike) {
    return this.activeCall() === call && this.callWasAccepted(call) === true;
  }

  private clearCallQuality() {
    this.activeQualityWarnings?.clear();
    this.lastQualitySample = undefined;
    this.lastQualityWriteAt = 0;
    this.callQuality?.set(undefined);
  }

  private updateCallQuality(sample?: unknown) {
    if (sample && typeof sample === 'object') this.lastQualitySample = sample as Record<string, unknown>;
    const latest = this.lastQualitySample || {};
    const recovering = this.threeWayConnectionRecovering();
    if (!this.lastQualitySample && !recovering) return;
    const metric = (key: string) => typeof latest[key] === 'number' && Number.isFinite(latest[key] as number) ? latest[key] as number : undefined;
    const rtt = metric('rtt'), jitter = metric('jitter'), loss = metric('packetsLostFraction'), mos = metric('mos');
    const poorWarnings = ['low-mos', 'high-packet-loss', 'high-packets-lost-fraction', 'high-rtt', 'high-jitter', 'ice-connectivity-lost'];
    const level: SoftphoneCallQualityLevel = recovering ? 'reconnecting'
      : poorWarnings.some(name => this.activeQualityWarnings?.has(name)) ? 'poor'
        : (mos !== undefined && mos < 4) || (loss !== undefined && loss > 1) || (rtt !== undefined && rtt > 250) ? 'fair'
          : 'good';
    const next: SoftphoneCallQuality = {
      level,
      rttMs: rtt === undefined ? undefined : Math.round(rtt),
      jitterMs: jitter === undefined ? undefined : Math.round(jitter),
      lossPct: loss === undefined ? undefined : Math.round(loss * 10) / 10,
      mos: mos === undefined ? undefined : Math.round(mos * 10) / 10,
    };
    const current = this.callQuality();
    const now = Date.now();
    // Level changes show immediately; metric-only changes refresh the tooltip at a bounded rate.
    if (current && current.level === next.level
      && (now - this.lastQualityWriteAt < this.qualityMetricRefreshMs
        || (current.rttMs === next.rttMs && current.jitterMs === next.jitterMs && current.lossPct === next.lossPct && current.mos === next.mos))) return;
    this.lastQualityWriteAt = now;
    this.callQuality.set(next);
  }

  private completeQualityAttempt(id: string, outcome: string) {
    const context = this.getDiagnostics().captureContext(id);
    if (!this.getDiagnostics().finishCapture(id, outcome)) return;
    const workspaceId = typeof context?.['workspaceId'] === 'string' ? context['workspaceId'] : undefined;
    if (!this.softphoneService.isOwnerTab || !this.softphoneService.qualityTelemetryEnabled) return;
    if (this.automaticReport) { this.getDiagnostics().recordAutomaticOutcome('skipped-busy'); return; }
    const userId = this.user()?._id?.toString();
    const operation = { id: crypto.randomUUID(), abort: new AbortController(), timer: undefined as any };
    const deadline = performance.now() + 15_000;
    operation.timer = setTimeout(() => operation.abort.abort(), 15_000);
    this.automaticReport = operation;
    queueMicrotask(() => void (async () => {
      let debug: any;
      try {
        debug = await this.buildSoftphoneDebugSnapshot(true);
        if (operation.abort.signal.aborted || this.user()?._id?.toString() !== userId || !this.softphoneService.isOwnerTab) return;
        const response = await this.app.reportIssue({ category: 'softphone', submissionSource: 'automatic', terminalOutcome: outcome, workspaceId,
          description: '', debug, url: window.location.href, userAgent: navigator.userAgent, clientTimestamp: new Date() },
          { remainingMs: deadline - performance.now(), signal: operation.abort.signal });
        if (operation.abort.signal.aborted || this.user()?._id?.toString() !== userId || !this.softphoneService.isOwnerTab) return;
        this.getDiagnostics().recordAutomaticOutcome(response.outcome || 'submitted');
        this.onSoftphoneReportSubmitted(response, debug);
      } catch { if (this.user()?._id?.toString() === userId && this.automaticReport === operation) this.getDiagnostics().recordAutomaticOutcome(operation.abort.signal.aborted ? 'unknown-cancelled' : 'unknown-failed'); }
      finally {
        debug = undefined; clearTimeout(operation.timer);
        if (this.automaticReport === operation) this.automaticReport = undefined;
      }
    })());
  }

  private cancelAutomaticReport() {
    const operation = this.automaticReport;
    if (!operation) return;
    this.getDiagnostics().recordAutomaticOutcome('unknown-cancelled');
    operation.abort.abort(); clearTimeout(operation.timer); this.automaticReport = undefined;
  }

  onSoftphoneReportSubmitted = (response: IssueReportResponse, debug: any) => {
    if (response.outcome !== 'submitted' || !response.issueId || response.debugFileStatus !== 'available') return;
    const ids = debug?.callQuality?.captures?.filter((c: any) => c.endedAt).map((c: any) => c.captureId) || [];
    try {
      if (this.softphoneService.isOwnerTab) this.getDiagnostics().acknowledgeCaptures(ids);
      else if (debug?.callQuality?.ownerInstanceId === this.sessionState().ownerInstanceId)
        this.softphoneService.sendOwnerCommand({ type: 'cleanup-call-quality', payload: { captureIds: ids } });
    } catch { this.getDiagnostics().recordAutomaticOutcome('cleanup-failed'); }
  };

  private requestOwnerQuality(): Promise<unknown> {
    if (!this.sessionState().ownerInstanceId) return Promise.resolve({ version: 1, captureSource: 'unavailable', captures: [] });
    const command = this.softphoneService.sendOwnerCommand({ type: 'capture-call-quality', payload: {} });
    return new Promise(resolve => {
      const timer = setTimeout(() => { subscription.unsubscribe(); resolve({ version: 1, captureSource: 'unavailable', captures: [] }); }, 2000);
      const subscription = this.softphoneService.ownerCommandResult$.subscribe(result => {
        if (result.messageId !== command.messageId || result.userId !== command.userId || result.ownerInstanceId !== command.targetOwnerInstanceId
          || result.sourceTabId !== this.sessionState().ownerTabId) return;
        clearTimeout(timer); subscription.unsubscribe();
        resolve({ ...(result.payload?.callQuality as any), ownerInstanceId: result.ownerInstanceId });
      });
    });
  }

  buildSoftphoneDebugSnapshot = async (automatic = false) => {
    this.refreshQualityContexts();
    const frozenQuality = this.getDiagnostics().qualitySnapshot();
    const workspace = this.currentWorkspace();
    const runtimeDiagnostics = this.softphoneService.twilioDiagnostics;
    const diagnostics = this.getDiagnostics();
    this.updateDiagnosticTokenFetchAt(runtimeDiagnostics.lastTokenFetchAt);
    const diagnosticMetadata = this.diagnosticsState();

    const connection: any = (navigator as any).connection || undefined;
    let micPermission: string | undefined = this.softphoneService.microphonePermission;
    try {
      if (!automatic && (navigator as any).permissions?.query) {
        const p = await (navigator as any).permissions.query({ name: 'microphone' as any });
        micPermission = p.state;
        this.softphoneService.microphonePermission = p.state;
      }
    } catch (error) {
      this.exceptions.record(error, { source: 'softphone', operation: 'diagnostic-microphone-permission' });
      micPermission = undefined;
    }

    const sdkVersion = (this.device?.constructor as typeof Device)?.version || (window as any).Twilio?.Device?.version || undefined;
    const activeCall = this.activeCall() || this.incomingCall();
    const activeCallSid = this.getCallSid(activeCall);
    const mirrored = this.sessionState();
    const incomingTransfer = this.mirroredIncomingTransfer();
    const transferContext = this.transferContext();
    const activeCallStatus = typeof (activeCall as any)?.status === 'function'
      ? (activeCall as any).status()
      : (activeCall as any)?.status;

    return diagnostics.createSupportReportSnapshot({
      schemaVersion: 5,
      callQuality: this.softphoneService.isOwnerTab || automatic ? frozenQuality : await this.requestOwnerQuality(),
      capturedAt: new Date(),
      toastHistory: this.app.getToastHistory(),
      appErrorHistory: this.app.getAppErrorHistory(),
      ...this.exceptions.snapshot(),
      environment: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        timezone: (Intl as any).DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine,
        connection: connection ? { effectiveType: connection.effectiveType, downlink: connection.downlink } : undefined,
        microphonePermission: micPermission,
      },
      user: this.user() ? { _id: this.user()?._id, email: this.user()?.email, profile: this.user()?.profile } : undefined,
      workspace: workspace ? { _id: workspace._id, name: workspace.name, ownerId: workspace.ownerId } : (this.workspaceService.workspaceId ? { _id: this.workspaceService.workspaceId } : undefined),
      twilio: {
        identity: this.device?.identity,
        sdkVersion,
        deviceState: (this.device as any)?.state,
        deviceSid: (this.device as any)?.sid || undefined,
        activeCallSid,
        lastTokenFetchAt: diagnosticMetadata.lastTokenFetchAt,
        reconnectAttempts: diagnosticMetadata.reconnectCount,
        lastSdkEvents: diagnosticMetadata.recentSdkEvents,
      },
      runtime: {
        ownerRecovery: runtimeDiagnostics.ownerRecovery,
        coachCorrelationStages: runtimeDiagnostics.coachCorrelationStages,
        coachAdmissions: runtimeDiagnostics.coachAdmissions,
        local: {
          state: this.state(),
          detail: this.stateDetail(),
          browserSessionMode: this.browserSessionMode(),
          activeBrowserSession: this.activeBrowserSession(),
          browserSessionEvents: this.softphoneService.browserSessionDiagnostics,
          enabled: this.enabled(),
          selectedIntegrationId: this.selectedIntegrationId(),
          isOwner: this.softphoneService.isOwnerTab,
          callProgress: this.callProgress(),
          activeCall: activeCall ? {
            callSid: activeCallSid,
            callId: this.callState().voxrCallId || this.getCallParam(activeCall, 'CallId'),
            transferId: this.getCallParam(activeCall, 'TransferId'),
            integrationId: this.getCallParam(activeCall, 'IntegrationId'),
            status: activeCallStatus,
          } : undefined,
        },
        mirrored: {
          tabId: mirrored.tabId,
          ownerTabId: mirrored.ownerTabId,
          ownerInstanceId: mirrored.ownerInstanceId,
          isOwner: mirrored.isOwner,
          state: mirrored.state,
          detail: mirrored.detail,
          setupIssue: mirrored.setupIssue,
          ownerLifecycleStatus: mirrored.ownerLifecycleStatus,
          lastSnapshotPublishedAt: mirrored.lastSnapshotPublishedAt,
          lastSnapshotReason: mirrored.lastSnapshotReason,
          ownerRecovery: mirrored.ownerRecovery,
          hasPartialConnectionWarning: mirrored.hasPartialConnectionWarning,
          selectedIntegrationId: mirrored.selectedIntegrationId,
          activeCall: mirrored.activeCall,
          browserSessionMode: mirrored.browserSessionMode,
          activeBrowser: mirrored.activeBrowser,
          ownerLastHeartbeatAt: mirrored.ownerLastHeartbeatAt,
          stale: mirrored.stale,
          connections: mirrored.connections?.map(item => ({
            integrationId: item.integrationId,
            accountSid: item.accountSid,
            status: item.status,
            deviceSid: item.deviceSid,
            error: item.error,
            lastSeenAt: item.lastSeenAt,
            retryAt: item.retryAt,
          })),
          incomingTransfer: incomingTransfer ? {
            targetTabId: incomingTransfer.targetTabId,
            callId: incomingTransfer.call?._id,
            campaignId: incomingTransfer.campaign?._id,
            transferId: incomingTransfer.transfer?._id,
            integrationId: incomingTransfer.transfer?.integrationId || incomingTransfer.call?.integrationId,
          } : undefined,
        },
        server: this.softphoneStateDto(),
      },
      context: {
        activeTransfer: this.activeTransfer() ? {
          id: this.activeTransfer()?._id,
          callId: this.activeTransfer()?.callId,
          integrationId: this.activeTransfer()?.integrationId,
          status: this.activeTransfer()?.status,
        } : undefined,
        transfer: transferContext ? {
          callId: transferContext.call?._id,
          campaignId: transferContext.campaign?._id,
          transferId: transferContext.transfer?._id,
          integrationId: transferContext.transfer?.integrationId || transferContext.call?.integrationId,
        } : undefined,
        lead: this.callLeadContext(),
      },
    });
  };
}

